/**
 * Match state machine + shared restart plumbing. (§5.1)
 *
 * `stepMatchStateMachine` runs EARLY in the tick (step.ts stage 2), before any
 * player control or ball integration. Its jobs:
 *
 *   1. Drive every restart through setup → ready → live (§5.1). A restart's
 *      SETUP phase enforces positioning and gates inputs; its READY phase lets
 *      the designated taker play the ball; going LIVE returns the world to
 *      MatchPhase.InPlay so kick resolution (stage 6) can strike the ball.
 *   2. Gate which inputs are legal during a restart — you cannot shoot or tackle
 *      during a throw-in setup. This is done by clearing the illegal action bits
 *      on the passed InputCommand objects for this tick.
 *   3. Advance halves: end a half when the clock (plus added time) runs out,
 *      break at half-time, flip ends, and end the match at full time.
 *
 * The `begin*` helpers below are the single seam every rule uses to STOP play
 * and stage a restart; ball-out.ts, fouls.ts and offside.ts all route through
 * `beginRestart`, so restart bookkeeping (stage timer, taker selection, ball
 * placement, advantage cancellation) lives in exactly one place.
 *
 * Two restart phases get EXTRA staging on top of the generic Setup→Ready→Live
 * machine (Phase 4B/4C, playtest DEFECTS #7/#8/#9):
 *
 *   - Kickoff: `enforceKickoffFormation` re-pins every player into their own
 *     half and pushes the non-taking team outside the centre circle, every
 *     Setup/Ready tick (Law 8). Nothing previously enforced this — the AI's
 *     decideRestart in individual.ts only *voluntarily* seeks a legal spot;
 *     a human (or a slow AI) could stand on the ball. This is the same
 *     "freeze until live" idea `gateInputs` already applies to input bits,
 *     applied to POSITION instead, so it self-heals every tick rather than
 *     inventing a parallel hard-wall mechanism.
 *   - ThrowIn: never goes through the generic foot-strike Ready handling
 *     below (that hands a Pass-flagged input to ball.ts's resolveKicks, which
 *     is correct for a kick-off/corner/goal-kick/free-kick — they ARE kicks —
 *     but a throw-in is a two-handed overhead THROW, Law 15, and nobody ever
 *     wrote that path: the ball would sit dead through Ready and then the
 *     generic timeout just declared play live with a motionless ball on the
 *     touchline). `runThrowInReady` executes it directly: opponents are held
 *     `THROW_OPPONENT_DISTANCE` back (Law 15), and the taker's release is a
 *     traced impulse this file applies itself (`executeThrowIn`), never a
 *     foot strike. Bounded: if nobody releases it inside the Ready window,
 *     the FSM auto-throws with the same aiming heuristic — a staged restart
 *     can never deadlock.
 *
 * Zero allocation (§15.7): everything here is scalar arithmetic over the SoA
 * buffers. The only heap traffic is pushing the rare HALF_TIME / FULL_TIME
 * events, which happen a handful of times per match, never per tick.
 */

import type { InputCommand, Position, StepResult, WorldState } from '../types.js';
import { AnimState, InputFlags, MatchPhase, RestartStage, SkillMove, MAX_PLAYERS } from '../types.js';
import { beginAnimAction } from '../anim-exec.js';
import { BallVelocityCause, traceBallSince } from '../ball-trace.js';
import { DEG2RAD, clamp, datan2, dcos, dsin, rotateTowards } from '../../math/vec3.js';
import { beginStageLockTick, clearStageLock, setStageLock } from './stage-lock.js';
import { recordGkTravel } from '../gk-travel.js';
import { statMultiplier } from '../player-controller.js';
import {
  AI_MARK_GOALSIDE_DISTANCE,
  BALL_RADIUS,
  CORNER_COUNTER_OUTLET_X,
  CORNER_FAR_POST_DEPTH,
  CORNER_FAR_POST_Z,
  CORNER_GK_LINE_INSET,
  CORNER_GK_NEAR_POST_BIAS,
  CORNER_LATE_GAME_REMAINING_FRAC,
  CORNER_MARKER_COVER_DEPTH,
  CORNER_MARKER_COVER_SPREAD_Z,
  CORNER_NEAR_POST_DEPTH,
  CORNER_NEAR_POST_Z,
  CORNER_CENTRAL_TARGET_DEPTH,
  CORNER_OPPONENT_DISTANCE,
  CORNER_POST_DEFENDER_INSET,
  CORNER_REBOUND_DEPTH,
  CORNER_REBOUND_Z,
  CORNER_SHORT_OPTION_DEPTH,
  CORNER_SHORT_OPTION_WIDTH_INSET,
  CORNER_TAKER_INSET,
  FIXED_DT,
  FREE_KICK_SETUP_S,
  FREE_KICK_WALL_DISTANCE,
  FREE_KICK_WALL_FAR_DIST,
  FREE_KICK_WALL_MID_DIST,
  FREE_KICK_WALL_NEAR_DIST,
  FREE_KICK_WALL_SPACING,
  GOAL_CELEBRATION_S,
  HALF_TIME_BREAK_S,
  KICKOFF_DUP_SPREAD_FZ,
  KICKOFF_FORWARD_PULLBACK_FX,
  KICKOFF_OPPONENT_DISTANCE,
  KICKOFF_SETUP_S,
  KICKOFF_SHAPE_DEPTH,
  KICKOFF_TAKER_INSET,
  PENALTY_ARC_RADIUS,
  PENALTY_AREA_LENGTH,
  PENALTY_AREA_WIDTH,
  PENALTY_SETUP_S,
  PENALTY_TAKER_RUNUP,
  PENALTY_WAITER_MARGIN,
  ACCELERATION,
  DECELERATION,
  RESTART_READY_S,
  RESTART_REST_DEFEND_OWN_HALF_INSET,
  RESTART_REST_DEFEND_SPREAD_Z,
  RESTART_ROLE_AERIAL_WEIGHT,
  RESTART_CORNER_SAME_SIDE_BONUS,
  RESTART_CORNER_WIDE_WEIGHT,
  RESTART_TAKER_DISTANCE_WEIGHT,
  RESTART_ROLE_DISTANCE_WEIGHT,
  RESTART_ROLE_POSITION_WEIGHT,
  RESTART_SETUP_S,
  RESTART_STAGE_EXTRA_MAX_S,
  RESTART_STAGE_JOG_DIST,
  RESTART_STAGE_JOG_SPEED,
  RESTART_STAGE_ON_MARK_DIST,
  RESTART_STAGE_SETTLE_PAD_S,
  RESTART_STAGE_SPEED,
  RESTART_STAGE_TURN_RATE_DEG,
  RESTART_STAGE_WALK_DIST,
  RESTART_STAGE_WALK_SPEED,
  SIMULATION_TICK_RATE,
  THROW_ANGLE_DEG,
  THROW_ANGLE_LIFT_DEG,
  THROW_ANIM_HOLD_S,
  THROW_HANDS_BACK_OFFSET,
  THROW_PICKUP_S,
  THROW_CARRY_HEIGHT,
  THROW_CARRY_FRONT_OFFSET,
  THROW_WINDUP_RAISE_FRAC,
  THROW_RELEASE_CONTACT_S,
  THROW_BACKWARD_SAFETY_DEPTH,
  THROW_BACKWARD_SAFETY_WIDTH_INSET,
  THROW_FORWARD_SLACK,
  THROW_INSIDE_SUPPORT_DEPTH,
  THROW_INSIDE_SUPPORT_WIDTH_INSET,
  THROW_LINE_RUNNER_DEPTH,
  THROW_MARK_TIGHT_DIST,
  THROW_MAX_SPEED,
  THROW_MIN_SPEED,
  THROW_OPPONENT_DISTANCE,
  THROW_RELEASE_HEIGHT,
  THROW_SHORT_RECEIVER_DEPTH,
  THROW_SHORT_RECEIVER_WIDTH_INSET,
  THROW_SPEED_RANGE_MAX_DIST,
  THROW_SPEED_RANGE_MIN_DIST,
  THROW_TAKE_REACH,
  THROW_TAKER_INSET,
  THROW_TARGET_MAX_RANGE,
} from '../constants.js';
import { POSITION_ANCHORS } from '../formation-anchors.js';
import { beginShootout, stepShootout, stepShootoutAttempt } from './shootout.js';

// Scratch for runRestart's human save/restore (§Restart no-teleport). Reused —
// allocated once, never in the per-tick path. Only the humanMask==1 slots are
// ever read back, so stale entries for other slots are harmless.
const restartSaveX = new Float64Array(MAX_PLAYERS);
const restartSaveZ = new Float64Array(MAX_PLAYERS);
const restartSaveY = new Float64Array(MAX_PLAYERS);
const restartSaveVx = new Float64Array(MAX_PLAYERS);
const restartSaveVy = new Float64Array(MAX_PLAYERS);
const restartSaveVz = new Float64Array(MAX_PLAYERS);

// ---------------------------------------------------------------------------
// Restart WALK-IN staging (§5.1–§5.3, Laws 13/14/15/17)
//
// Every staging function below used to write its answer straight into px/pz.
// The SHAPE that produced was right, and is unchanged — but the way players
// GOT there was a one-frame teleport of up to ~80 m (the corner taker
// materialising on the flag; the wall appearing on the 9.15 m mark; everyone
// in the box vanishing to its edge for a penalty). That is the reported
// defect: "how players go instead of teleport".
//
// The fix separates WHERE from HOW:
//
//   1. Every staging function now writes a per-player MARK into the scratch
//      buffers below (setStageTarget) instead of a position. The role
//      selection, the Law clamps and the target geometry are exactly what was
//      there before — this changes how players arrive, not where.
//   2. applyStageTargets is the single mover: it steps each staged player a
//      capped distance toward his mark — the "the capped position write IS the
//      movement" discipline goalkeeper.ts's moveToward uses. The last partial
//      step lands EXACTLY on the mark, so a settled shape is still exact
//      rather than asymptotic.
//   3. sizeSetupWindowForWalkIn runs ONCE, in beginRestart, and sizes the
//      Setup window to the longest walk (clamped by RESTART_STAGE_EXTRA_MAX_S)
//      so play cannot resume before the shape has settled — while staying
//      strictly bounded, because a per-tick "hold until settled" gate has no
//      upper bound and one stuck player would hang the match forever.
//
// ── The DRAGGING regression, and why the mechanism changed ──────────────────
//
// The first version of (2) also ZEROED each staged player's velocity, so that
// step.ts stage 4 could not integrate it and move him a second time. Owner
// report: *"when corner or throw in happens bots are getting dragged they are
// not running / please make sure each and every player runs or walks or sprint
// / no forced dragging"*. Correct, and it was this.
//
// Velocity is not private bookkeeping — it is the sim's statement of what a
// player is DOING, and the rest of the engine reads it. player-controller.ts
// stepAnim picks the locomotion clip from |v| alone, so v = 0 ⇒ AnimState.Idle:
// a player being stepped 0.1 m per tick was rendered STANDING STILL while his
// position translated. stepStamina bands effort by |v|, so the walk-in was free.
// ai/** leads passes off teammate velocity and saw a pitch full of statues.
//
// So the velocity is now HONEST — direction × the pace actually applied this
// tick, giving |v| = |Δp| / FIXED_DT by construction — and the double-move is
// prevented by GATING the staged player's own locomotion instead
// (rules/stage-lock.ts; player-controller.ts consumes it). applyStageTargets
// therefore owns a staged player's position, velocity AND facing for that tick,
// and owns them completely.
//
// It also applies a GAIT LADDER rather than one uniform speed: he RUNS the long
// haul, eases to a JOG inside RESTART_STAGE_JOG_DIST and WALKS the last stride
// (stageSpeedForDistance). sizeSetupWindowForWalkIn budgets with the SAME ladder
// (stageTravelTime) — sizing the window with a speed the mover does not use is
// how a ceremony ends with its shape still in transit.
//
// KICK-OFF is deliberately NOT in this scheme. It is a hard reset (match start
// / after a goal, behind a GOAL_CELEBRATION_S dwell), it is the one restart
// runRestart already refuses to spare humans from, and
// restart-no-teleport.test.ts pins that contract explicitly. It keeps its
// instant re-pin.
//
// These buffers are module scratch, NOT WorldState: they are rebuilt from
// scratch every tick by computeStageTargets and never read across ticks, so
// they are not hashed, cloned or serialized and cannot desync (§15.3, D4).
// ---------------------------------------------------------------------------

const stageTargetX = new Float64Array(MAX_PLAYERS);
const stageTargetZ = new Float64Array(MAX_PLAYERS);
const stageHasTarget = new Uint8Array(MAX_PLAYERS);
/**
 * Is this mark part of the CEREMONY — something the restart must actually wait
 * for? The taker at the flag, the wall on its line, the box emptying for a
 * penalty, the runners taking up their posts: yes. A spare man trotting back to
 * his own half for rest defence: NO — in real football he is still jogging home
 * as the corner is swung in, and nobody holds up the kick for him.
 *
 * Only critical marks size the Setup window (maxStageTravel). Without this
 * distinction a corner ALWAYS ran to the RESTART_STAGE_EXTRA_MAX_S cap
 * (MEASURED: 13.39s from realistic in-play positions), because a rest-defence
 * mark is by construction the length of the pitch away from a corner flag.
 * With it, the same corner takes ~4s and the spare men keep jogging back
 * through the Ready window and into open play, which is exactly right.
 */
const stageTargetCritical = new Uint8Array(MAX_PLAYERS);

/**
 * How far off his mark a staged player may be and still count as SET.
 * applyStageTargets lands the final partial step EXACTLY on the mark, so this
 * absorbs float noise only — it is deliberately far too small to hide a player
 * who is genuinely still a stride out.
 */
const RESTART_STAGE_SETTLED_EPSILON = 1e-6;

/** Forget last tick's marks. Called at the top of every computeStageTargets. */
function clearStageTargets(): void {
  stageHasTarget.fill(0);
  stageTargetCritical.fill(0);
}

/**
 * Send player `i` to (x,z) — a MARK to walk to, not a position to snap to.
 * `critical` (default true) is whether the restart waits for him; see
 * stageTargetCritical.
 */
function setStageTarget(i: number, x: number, z: number, critical = true): void {
  stageTargetX[i] = x;
  stageTargetZ[i] = z;
  stageHasTarget[i] = 1;
  stageTargetCritical[i] = critical ? 1 : 0;
}

/**
 * Where player `i` is currently headed: his staged mark if a role already gave
 * him one this tick, otherwise where he actually stands. Law clamps compose on
 * top of role targets this way — the clamp adjusts the MARK, so a defender the
 * choreography sent somewhere illegal walks to the legal spot instead of being
 * placed illegally and then shoved out of it.
 */
function stageBaseX(state: WorldState, i: number): number {
  return stageHasTarget[i] === 1 ? stageTargetX[i]! : state.players.px[i]!;
}
function stageBaseZ(state: WorldState, i: number): number {
  return stageHasTarget[i] === 1 ? stageTargetZ[i]! : state.players.pz[i]!;
}

/**
 * The GAIT LADDER: how fast a staged player travels with `remaining` metres
 * still to cover. A step function, not a ramp, and deliberately so:
 *
 *   - Each band's speed sits mid-band inside one locomotion anim band
 *     (constants.ts), so the clip is unambiguous and cannot flicker.
 *   - Its integral has an exact closed form in +−×÷ (stageTravelTime). A smooth
 *     ramp's does not: ds/dt = −v(s) with v linear in s integrates to a
 *     logarithm, and Math.log is not IEEE-754 correctly rounded, so putting one
 *     on the path that sizes `match.stageTimer` — a HASHED field — would import
 *     a cross-engine determinism hazard for a cosmetic curve (§15.3, and the
 *     same argument math/vec3.ts's dcos/dsin wrappers exist for).
 */
function stageSpeedForDistance(remaining: number): number {
  if (remaining <= RESTART_STAGE_WALK_DIST) return RESTART_STAGE_WALK_SPEED;
  if (remaining <= RESTART_STAGE_JOG_DIST) return RESTART_STAGE_JOG_SPEED;
  return RESTART_STAGE_SPEED;
}

/**
 * Seconds a staged player needs to cover `distance` metres under
 * stageSpeedForDistance — the exact integral of the ladder above, band by band.
 *
 * This MUST stay the inverse of the mover, or the Setup window gets sized with a
 * pace nobody travels at: the run band alone (distance/RESTART_STAGE_SPEED, what
 * this used to be) under-counts every walk-in by the 2.75 s the jog and walk
 * bands cost, and the ceremony would end with its last men still arriving.
 */
function stageTravelTime(distance: number): number {
  if (distance <= 0) return 0;
  const walkBandS = RESTART_STAGE_WALK_DIST / RESTART_STAGE_WALK_SPEED;
  if (distance <= RESTART_STAGE_WALK_DIST) return distance / RESTART_STAGE_WALK_SPEED;
  if (distance <= RESTART_STAGE_JOG_DIST) {
    return (distance - RESTART_STAGE_WALK_DIST) / RESTART_STAGE_JOG_SPEED + walkBandS;
  }
  const jogBandS = (RESTART_STAGE_JOG_DIST - RESTART_STAGE_WALK_DIST) / RESTART_STAGE_JOG_SPEED;
  // ── THE COST OF ACCELERATING RATHER THAN BEING GRANTED PACE ───────────────
  //
  // The mover now ramps into RESTART_STAGE_SPEED under ACCELERATION instead of
  // being handed it (see applyStageTargets). Reaching v from rest costs v/a and
  // covers v²/2a, where travelling that same distance at v would have cost
  // v²/2a / v = v/2a — so the ramp adds exactly **v / (2a)** seconds, a
  // CONSTANT: 5.5 / 18 = 0.306 s.
  //
  // Kept in +−×÷ deliberately. A smooth ramp's exact integral is a logarithm,
  // `Math.log` is not IEEE-754 correctly rounded, and this feeds `stageTimer`,
  // which is HASHED — that is the same cross-engine determinism argument the
  // gait ladder's step shape was chosen for (§15.3, D2).
  //
  // Conservative on purpose in two ways, both erring toward a LONGER window,
  // which is the safe direction (the unsafe one ends the ceremony with men
  // still walking):
  //   - it charges the full ramp even for a player already moving when staging
  //     began, who pays less;
  //   - it ignores the band step-downs (5.5→3.0→1.2), which now decelerate
  //     rather than dropping instantly and therefore cover their ground FASTER
  //     than this estimate assumes.
  // Per-player Acceleration stat variation rides inside RESTART_STAGE_SETTLE_PAD_S.
  const rampS = RESTART_STAGE_SPEED / (2 * ACCELERATION);
  return (distance - RESTART_STAGE_JOG_DIST) / RESTART_STAGE_SPEED + jogBandS + walkBandS + rampS;
}

/**
 * Swing a staged player's body toward `targetYaw` at RESTART_STAGE_TURN_RATE_DEG.
 *
 * Load-bearing, not decoration. Because a staged player's own locomotion is
 * gated off (stage-lock.ts), nothing else rotates him — and locomotionAnim
 * compares facing against the direction of travel, showing a STRAFE clip beyond
 * STRAFE_ANGLE_COS (60°). A frozen yaw would therefore trade the old
 * "gliding while idle" bug for a new "shuffling sideways to the mark" one.
 * Rotating at a real rate also means he visibly TURNS and sets off, rather than
 * snapping onto his new bearing.
 */
function turnStagedPlayer(state: WorldState, i: number, targetYaw: number): void {
  const p = state.players;
  p.yaw[i] = rotateTowards(p.yaw[i]!, targetYaw, RESTART_STAGE_TURN_RATE_DEG * DEG2RAD * FIXED_DT);
}

/**
 * Walk every staged player one tick toward his mark, at the gait that distance
 * deserves, carrying the velocity and the facing that motion really implies.
 *
 * Players with no mark this tick (a free kick's ATTACKING side, say) are not
 * touched at all — exactly as before, they are free to move as they like.
 *
 * Every player this function moves is STAGE-LOCKED for the tick (see
 * rules/stage-lock.ts): his own controller must not run its locomotion on top,
 * or stage 4 would integrate the velocity written here and move him twice. That
 * gate is what lets the velocity be true instead of zero — the whole point of
 * this rewrite (see the DRAGGING note in the section header).
 */
function applyStageTargets(state: WorldState): void {
  const p = state.players;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (stageHasTarget[i] !== 1) continue;
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;

    // His locomotion belongs to the ceremony from here on, moving or not: a man
    // stood on his mark must HOLD it until the whistle, not wander off it and be
    // dragged back next tick.
    setStageLock(i);

    const dx = stageTargetX[i]! - p.px[i]!;
    const dz = stageTargetZ[i]! - p.pz[i]!;
    const d = Math.sqrt(dx * dx + dz * dz);

    if (d <= RESTART_STAGE_ON_MARK_DIST) {
      // ON HIS MARK. Genuinely still — v = 0 here is the truth, not a lie, and
      // stepAnim will correctly show him standing waiting for the whistle.
      p.px[i] = stageTargetX[i]!;
      p.pz[i] = stageTargetZ[i]!;
      p.vx[i] = 0; p.vy[i] = 0; p.vz[i] = 0;
      // Set-piece etiquette: a player who is set watches the ball. Also what
      // stops him keeping the bearing of the walk he has just finished, which
      // would leave a whole box of players facing outward.
      turnStagedPlayer(state, i, datan2(state.match.restartZ - p.pz[i]!, state.match.restartX - p.px[i]!));
      continue;
    }

    // ── PACE IS ACCELERATED, NOT GRANTED (§10.1 never-superhuman) ────────────
    //
    // Owner: *"make sure its fair for all"*. This is that.
    //
    // The gait ladder used to be applied as an ASSIGNMENT: a staged player was
    // simply given `stageSpeedForDistance(d)` on the tick he acquired a mark.
    // MEASURED (`locomotion-honesty.test.ts`, all staged outfielders over a real
    // 11v11): the worst single-tick speed change was **5.500 m/s — exactly
    // RESTART_STAGE_SPEED, from a standstill, in one tick** — while a player
    // moving under his own control, human or AI, is rate-limited by
    // `ACCELERATION` to 0.150 m/s per tick. That is a 36.7x instantaneous
    // advantage, and because `runRestart` restores humans after staging, ONLY a
    // bot could ever get it. It is also four velocity discontinuities per
    // walk-in (0→5.5, 5.5→3.0, 3.0→1.2, 1.2→0), each one an animation pop.
    //
    // He now obeys the same law `stepLocomotion` applies to everyone else, with
    // his OWN Acceleration attribute — so a quick player still gets there
    // quicker, which is the fair version of the same advantage.
    //
    // The previous speed is read straight off `p.vx/p.vz`, which this function
    // wrote last tick as `move / FIXED_DT`. No new state, nothing extra to hash:
    // the velocity IS the staging speed, which is precisely the property the
    // no-dragging rewrite established.
    const prevSpeed = Math.sqrt(p.vx[i]! * p.vx[i]! + p.vz[i]! * p.vz[i]!);
    const wantSpeed = stageSpeedForDistance(d);
    const paced = wantSpeed > prevSpeed
      ? Math.min(prevSpeed + ACCELERATION * statMultiplier(state.attributes[i]!.acceleration) * FIXED_DT, wantSpeed)
      // Deceleration is not stat-modulated, exactly as in stepLocomotion:
      // stopping is physics, not a stat.
      : Math.max(prevSpeed - DECELERATION * FIXED_DT, wantSpeed);

    // The step actually applied this tick, and the velocity that IS that step.
    // Written as (move / FIXED_DT) rather than (unit × speed) so the identity
    // the no-dragging test asserts — |v| === |Δp| / FIXED_DT — holds to float
    // rounding rather than merely approximately.
    const stepLen = Math.min(paced * FIXED_DT, d);
    const scale = stepLen / d;
    // Land exactly on the mark rather than creeping toward it forever — a
    // settled set piece is an exact shape, and the suites that pin that shape
    // (corner near/far post, the wall on the line) measure it to centimetres.
    const moveX = stepLen >= d ? dx : dx * scale;
    const moveZ = stepLen >= d ? dz : dz * scale;
    p.px[i] = stepLen >= d ? stageTargetX[i]! : p.px[i]! + moveX;
    p.pz[i] = stepLen >= d ? stageTargetZ[i]! : p.pz[i]! + moveZ;

    // ── THE GOALKEEPER CARVE-OUT IS RETIRED (2026-08-09, locomotion honesty) ──
    //
    // He used to be the one player left here with a ZEROED velocity, and the
    // reason was explicit: goalkeeper.ts's `setStanceAnim` stamped
    // `AnimState.GkIdle` at stage 5 over whatever stage 4 derived, so an honest
    // velocity bought him no gait — only a keeper provably MOVING while the
    // state called him set. That was measured at 83 ticks of (|v| > 1.7 ∧
    // GkIdle) and the note handed the fix to goalkeeper.ts.
    //
    // That fix has landed: `setStanceAnim` now defers to `locomotionAnim` above
    // walking pace, so the contradiction the carve-out was avoiding cannot
    // occur, and he gets a real gait from a real velocity like everyone else.
    //
    // Double-integration was never the reason for the zeroing HERE either —
    // `setStageLock(i)` above already freezes stage 4's horizontal integration
    // for every player this function moves, keeper included. And `resolveSave`,
    // which reads keeper speed as stretch, cannot run during a restart at all:
    // `stepKeeper` returns on `!live` before reaching it.
    //
    // He still records his step, because `moveToward` (open play) genuinely does
    // zero his velocity and the clip chooser takes whichever of the two is real.
    if (state.positions[i] === 'GK') recordGkTravel(i, moveX, moveZ);

    p.vx[i] = moveX / FIXED_DT;
    p.vz[i] = moveZ / FIXED_DT;
    // Horizontal only: the walk-in has no vertical component, and leaving vy
    // behind would let the character controller's gravity term accumulate across
    // a 13-second ceremony.
    p.vy[i] = 0;
    turnStagedPlayer(state, i, datan2(moveZ, moveX));
  }
}

/**
 * Furthest any CEREMONY-CRITICAL staged player still has to walk, metres; 0 if
 * nobody is. Non-critical marks (rest defence, the corner's counter outlet, and
 * since 2026-08-09 every attacking receiver) are excluded — see
 * stageTargetCritical.
 *
 * ── WHY THIS IS NOW, IN PRACTICE, JUST THE TAKER (2026-08-09, dwell) ────────
 *
 * The owner reports the game feels slow, and restart dwell measured as the
 * largest single cause of it. This function is the thing that sets the length of
 * the ceremony: `sizeSetupWindowForWalkIn` turns it into the Setup window, and
 * the referee's whistle waits for it.
 *
 * MEASURED before the change, three restarts, ball-in-play time from the award:
 *
 *   throw-in (near)  taker arrived  9.67 s  →  whistle 13.07 s  →  live 15.40 s
 *   throw-in (far)   taker arrived  4.45 s  →  whistle 13.80 s  →  live 16.13 s
 *   corner           taker arrived 16.72 s  →  whistle 17.48 s  →  live 19.50 s
 *
 * Look at the far throw-in: the taker is stood over the ball at 4.45 s and the
 * game makes him wait 9.35 SECONDS for other people to finish walking. The
 * corner is pinned at its 17.5 s cap for the same reason. That is the dead air.
 *
 * The rule that replaces it: **the only arrival the whistle may wait for is the
 * man who takes it.** Everyone else is shape, and shape arriving late is not a
 * defect — a receiver still moving as the ball is delivered is what these
 * restarts actually look like. The Law 15 / Law 17 distances are NOT waited for
 * either, and do not need to be: `enforceThrowInDistance` and
 * `pushStageTargetOutsideRadius` hold them as a PER-TICK constraint on wherever
 * a player currently is, so they are satisfied continuously rather than reached.
 *
 * Implementation note: this still reads the `stageTargetCritical` flags rather
 * than hard-coding `restartTaker`, because the flag is the honest expression of
 * "the ceremony needs this man in place" and a future restart may add another
 * such man (a penalty taker's keeper on his line is the obvious candidate).
 */
function maxStageTravel(state: WorldState): number {
  const p = state.players;
  let worst = 0;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (stageHasTarget[i] !== 1 || stageTargetCritical[i] !== 1) continue;
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    const dx = stageTargetX[i]! - p.px[i]!;
    const dz = stageTargetZ[i]! - p.pz[i]!;
    const d = Math.sqrt(dx * dx + dz * dz);
    if (d > worst) worst = d;
  }
  return worst;
}

// ---------------------------------------------------------------------------
// Geometry helpers — pure, allocation-free. Shared with the other rule files.
// ---------------------------------------------------------------------------

/**
 * Sign of the axis a team attacks along. +1 = toward +X, -1 = toward -X.
 * Team A attacks +X when `teamAAttacksPositiveX`; ends flip at half time.
 */
export function attackDir(state: WorldState, team: number): number {
  return (team === 0) === state.teamAAttacksPositiveX ? 1 : -1;
}

/** X of the goal `team` is attacking. */
export function attackGoalX(state: WorldState, team: number): number {
  return attackDir(state, team) * state.config.pitchLength * 0.5;
}

/** X of the goal `team` is defending. */
export function defendGoalX(state: WorldState, team: number): number {
  return -attackDir(state, team) * state.config.pitchLength * 0.5;
}

/** True while the ball is in the "restart is being staged" family of phases. */
export function isRestartPhase(phase: MatchPhase): boolean {
  return (
    phase === MatchPhase.Kickoff ||
    phase === MatchPhase.ThrowIn ||
    phase === MatchPhase.Corner ||
    phase === MatchPhase.GoalKick ||
    phase === MatchPhase.FreeKick ||
    phase === MatchPhase.Penalty ||
    phase === MatchPhase.DropBall
  );
}

/** Setup-window length for a given restart, seconds. (§5.1, §5.3) */
export function setupDurationFor(phase: MatchPhase): number {
  if (phase === MatchPhase.Kickoff) return KICKOFF_SETUP_S;
  if (phase === MatchPhase.FreeKick) return FREE_KICK_SETUP_S;
  if (phase === MatchPhase.Penalty) return PENALTY_SETUP_S;
  return RESTART_SETUP_S;
}

// ---------------------------------------------------------------------------
// Shared restart plumbing
// ---------------------------------------------------------------------------

/** Accumulate stoppage time (§5.5). Whole ticks so the hashed clock stays exact. */
export function addStoppage(state: WorldState, seconds: number): void {
  state.match.addedTimeTicks += Math.round(seconds * SIMULATION_TICK_RATE);
}

/** Nearest active, not-sent-off player of `team` to (x,z), or -1. */
export function nearestActivePlayerOfTeam(
  state: WorldState,
  team: number,
  x: number,
  z: number,
): number {
  const p = state.players;
  let best = -1;
  let bestSq = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i] !== team) continue;
    const dx = p.px[i]! - x;
    const dz = p.pz[i]! - z;
    const d = dx * dx + dz * dz;
    if (d < bestSq) {
      bestSq = d;
      best = i;
    }
  }
  return best;
}

/** Nearest active, not-sent-off OUTFIELD player of `team` to (x,z), or -1. */
function nearestOutfieldOfTeam(state: WorldState, team: number, x: number, z: number): number {
  const p = state.players;
  let best = -1;
  let bestSq = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i] !== team) continue;
    if (state.positions[i] === 'GK') continue;
    // ⚠ NOT THE MAN WHO WAS JUST FOULED. He is on the grass with a knockdown
    // clock running, and handing him the free kick is why a restart used to
    // sit there while everyone waited for him to get up (owner: "after
    // penality free kick should be properly given to the right player").
    if (p.knockdownTimer[i]! > 0) continue;
    const dx = p.px[i]! - x;
    const dz = p.pz[i]! - z;
    const d = dx * dx + dz * dz;
    if (d < bestSq) {
      bestSq = d;
      best = i;
    }
  }
  return best;
}

/**
 * How much this position looks like a corner taker: wide players first.
 *
 * Deliberately NOT `attackMindedBias` — that ranks by how far up the pitch a man
 * plays, which puts a STRIKER top and is exactly the wrong answer for a corner:
 * the striker is who you want IN the six-yard box, not standing over the ball.
 * Wingers and wide midfielders take corners; wing-backs take them; a centre-back
 * takes one only if there is nobody else.
 */
function cornerTakerBias(pos: Position): number {
  switch (pos) {
    case 'LW':
    case 'RW':
      return 1.0;
    case 'LM':
    case 'RM':
      return 0.9;
    case 'LWB':
    case 'RWB':
      return 0.7;
    case 'CAM':
      return 0.6;
    case 'LB':
    case 'RB':
      return 0.5;
    case 'CM':
      return 0.4;
    case 'CDM':
      return 0.25;
    case 'ST':
    case 'CF':
      return 0.1; // wanted in the box, not over the ball
    case 'CB':
      return 0.05;
    case 'GK':
      return 0; // never scored — excluded from the pool
    default:
      return 0.45;
  }
}

/** True if this position plays on the same touchline as z (its sign). */
function isOnSideOf(pos: Position, z: number): boolean {
  const left = pos === 'LW' || pos === 'LM' || pos === 'LB' || pos === 'LWB';
  const right = pos === 'RW' || pos === 'RM' || pos === 'RB' || pos === 'RWB';
  if (!left && !right) return false;
  // Left-sided positions sit at negative fz in POSITION_ANCHORS (formations.ts).
  return left ? z < 0 : z > 0;
}

/**
 * WHO TAKES THIS RESTART. (§5.1; owner, 2026-07-25: "the ball throwin, corner
 * kick, goal kick should be done by respective people")
 *
 * Previously ONE line served every restart type: the nearest active player of the
 * awarded team, with no position filter and — the real bug — no GK filter. That is
 * roughly right for a throw-in and wrong for everything else:
 *
 *  - GOAL KICK. The spot is the six-yard-box edge, so the keeper is 0-6 m from it
 *    and any centre-back who was defending the cross beats him on distance. An
 *    outfielder took the goal kick. Now the KEEPER takes it, which is both the
 *    Law's intent and what a viewer expects. (team-ai.ts's ownsSlot has a matching
 *    exception so the keeper is actually DRIVEN to the ball — without it a
 *    GK-taken restart had no driver at all and expired on the Ready timeout with
 *    the ball sitting dead on the spot.)
 *  - CORNER. Nearest body won it, so a striker who chased the ball into the corner
 *    took his own corner and was then absent from the box (enforceCornerFormation
 *    excludes the taker from the six aerial roles). Now a WIDE player takes it,
 *    with a bonus for being on the corner's own side.
 *  - EVERY OTHER RESTART. The keeper is no longer eligible. A free kick just
 *    outside your own box genuinely elected him, and a GK taker had no driver, so
 *    this removes a stall as well as an implausibility. He remains the fallback if
 *    literally nobody else is available (ten men sent off), because a restart with
 *    no taker cannot be taken at all.
 *
 * Pure and deterministic: distance/position arithmetic only, no RNG, no trig.
 */
function pickRestartTaker(
  state: WorldState,
  phase: MatchPhase,
  team: number,
  x: number,
  z: number,
): number {
  // A goal kick is the keeper's, always — that is the whole point of the change.
  if (phase === MatchPhase.GoalKick) {
    const gk = findGoalkeeperOfTeam(state, team);
    if (gk >= 0) return gk;
  }

  if (phase === MatchPhase.Corner) {
    const cands = activeOutfieldCandidates(state, team, -1);
    let best = -1;
    let bestScore = Number.NEGATIVE_INFINITY;
    for (const i of cands) {
      const pos = state.positions[i]!;
      const dx = state.players.px[i]! - x;
      const dz = state.players.pz[i]! - z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      let score = cornerTakerBias(pos) * RESTART_CORNER_WIDE_WEIGHT - dist * RESTART_TAKER_DISTANCE_WEIGHT;
      if (isOnSideOf(pos, z)) score += RESTART_CORNER_SAME_SIDE_BONUS;
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    if (best >= 0) return best;
  }

  // ⚠ A PENALTY IS TAKEN BY YOUR BEST FINISHER, NOT BY WHOEVER IS CLOSEST
  // (owner, 2026-08-16: "the gameplay shouldnt look amature ... penality if
  // draw"). Everything else on this list is a restart you take from where you
  // stand; a penalty is a chosen moment, and picking the nearest body is the
  // single most amateur thing a football game can do with one. Highest
  // `shooting` on the field takes it, keeper excluded, knocked-down men
  // skipped.
  if (phase === MatchPhase.Penalty) {
    const p = state.players;
    let bestTaker = -1;
    let bestShot = -Infinity;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
      if (p.team[i] !== team || state.positions[i] === 'GK') continue;
      if (p.knockdownTimer[i]! > 0) continue;
      const shot = state.attributes[i]!.shooting;
      if (shot > bestShot) { bestShot = shot; bestTaker = i; }
    }
    if (bestTaker >= 0) return bestTaker;
  }

  // Throw-in, kickoff, free kick, drop ball: nearest man, keeper excluded.
  const outfield = nearestOutfieldOfTeam(state, team, x, z);
  if (outfield >= 0) return outfield;
  // Nobody but the keeper is left. A restart still needs a taker.
  return nearestActivePlayerOfTeam(state, team, x, z);
}

/**
 * Drop the ball dead at (x,z): stationary, grounded, unpossessed. Clears every
 * possession flag and any goalkeeper hold, so no downstream module sees a live
 * ball while a restart is being staged.
 */
export function placeBallForRestart(state: WorldState, x: number, z: number): void {
  const b = state.ball;
  b.px = x;
  b.pz = z;
  b.py = BALL_RADIUS;
  const rvx0 = b.vx, rvy0 = b.vy, rvz0 = b.vz;
  b.vx = 0; b.vy = 0; b.vz = 0;
  traceBallSince(state, BallVelocityCause.RuleRestart, -1, rvx0, rvy0, rvz0);
  b.wx = 0; b.wy = 0; b.wz = 0;
  b.qx = 0; b.qy = 0; b.qz = 0; b.qw = 1;
  b.grounded = 1;
  b.possessedBy = -1;
  b.gatherOwner = -1; // §1: a placed restart ball is nobody's to gather (not the pre-stoppage carrier's)
  const p = state.players;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    p.possessing[i] = 0;
    if (p.gkHoldStartTick[i]! >= 0) p.gkHoldStartTick[i] = -1;
  }
}

/**
 * STOP play and stage a restart of `phase` for `team`, taken from (x,z).
 * The single entry point every rule uses to award a restart — it sets the FSM
 * into SETUP, picks the nearest teammate as taker, drops the ball dead on the
 * spot, and cancels any advantage that was in flight.
 */
export function beginRestart(
  state: WorldState,
  phase: MatchPhase,
  team: number,
  x: number,
  z: number,
): void {
  const m = state.match;
  m.phase = phase;
  m.restartStage = RestartStage.Setup;
  m.stageTimer = setupDurationFor(phase);
  m.restartTeam = team;
  m.restartX = x;
  m.restartZ = z;
  m.possessionTeam = team;
  m.restartTaker = pickRestartTaker(state, phase, team, x, z);
  m.advantageTicks = 0;
  m.pendingFoulPlayer = -1;
  m.pendingFoulSeverity = 0;
  placeBallForRestart(state, x, z);
  // The ceremony lasts as long as the walk-in genuinely needs (bounded) —
  // must run LAST, with phase/spot/taker already written, because it stages
  // the marks to measure them.
  sizeSetupWindowForWalkIn(state);
}

/** Stage a kickoff for `team` at the centre spot. (§5.1) */
export function beginKickoff(state: WorldState, team: number): void {
  // ── WHICH KICK-OFFS WALK IN, AND WHY ONLY THOSE ───────────────────────────
  //
  // Read BEFORE beginRestart, which overwrites the phase.
  //
  // A teleport is only perceptible as a DISCONTINUITY from where a player just
  // was. The first kick-off of each half has no "just was": Warmup is the
  // squads' initial placement, and half-time swaps ends behind a break during
  // which nothing is on screen. Walking those in buys no honesty and costs real
  // dead time — MEASURED, sizing the match-start window to the walk pushed it to
  // ~17.5 s (the RESTART_STAGE_EXTRA_MAX_S cap), and the owner has separately
  // reported the game feels slow. So they keep the instant placement they have
  // always had.
  //
  // The kick-off AFTER A GOAL is the opposite case, and it is the one the owner
  // reported: 22 players who are standing somewhere, visible, mid-celebration,
  // jumping up to 40.24 m in a single tick. That one walks — and it costs
  // nothing, because the walk already ran through GOAL_CELEBRATION_S
  // (stageKickoffDuringDwell), so by here most of the distance is behind them.
  const fromGoal = state.match.phase === MatchPhase.Goal;

  beginRestart(state, MatchPhase.Kickoff, team, 0, 0);

  // Either way the shape is established on the SAME tick the phase becomes
  // Kickoff. All three callers invoke this from inside stepMatchStateMachine's
  // own switch and `return` immediately, so the branch that would otherwise
  // reach enforceKickoffFormation via runRestart does not run until next tick —
  // without this, one tick of a just-finished, illegal position would be
  // visible before the very next tick corrected it.
  if (fromGoal) {
    enforceKickoffFormation(state);
    return;
  }
  snapKickoffShape(state);
  // beginRestart sized the window for a walk-in that is not going to happen.
  // Put it back to the plain ceremony length so an instant kick-off does not
  // sit in a Setup window budgeted for a walk nobody is taking.
  state.match.stageTimer = setupDurationFor(MatchPhase.Kickoff);
}

/**
 * Place every player directly on his kick-off mark — the pre-2026-08-09
 * behaviour, kept ONLY for the first kick-off of a half (see beginKickoff).
 *
 * It reads the same marks the walk-in walks to, so the two paths cannot drift
 * apart: there is exactly one definition of the kick-off shape.
 */
function snapKickoffShape(state: WorldState): void {
  const p = state.players;
  computeStageTargets(state);
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (stageHasTarget[i] !== 1) continue;
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    p.px[i] = stageTargetX[i]!;
    p.pz[i] = stageTargetZ[i]!;
    p.py[i] = 0;
    // §9: the ball goes live from a still formation, not one coasting into it.
    p.vx[i] = 0; p.vy[i] = 0; p.vz[i] = 0;
  }
  clearStageTargets();
}

/**
 * Dwell on a goal before staging the kickoff. (§5.1)
 *
 * Deliberately NOT beginRestart(): a goal is not a restart. The ball stays
 * exactly where it finished — in the net — for the celebration, which is both
 * what real football looks like and what lets the net's verlet bulge (§6.7) be
 * seen at all. beginRestart would immediately teleport it to the centre spot.
 *
 * `restartTeam` carries the team that will kick off (the CONCEDING side), which
 * stepMatchStateMachine hands to beginKickoff when the timer expires.
 */
export function beginGoalCelebration(state: WorldState, kickoffTeam: number): void {
  const m = state.match;
  m.phase = MatchPhase.Goal;
  m.restartStage = RestartStage.Setup;
  m.stageTimer = GOAL_CELEBRATION_S;
  m.restartTeam = kickoffTeam;
  m.restartTaker = -1;
  m.possessionTeam = -1;
  m.advantageTicks = 0;
  m.pendingFoulPlayer = -1;
  m.pendingFoulSeverity = 0;

  // The ball nestles into the net: position stays where the goal was scored,
  // velocity dies. Leaving it BALLISTIC for the 5s dwell was measured live to
  // be how a net-tunneling shot sailed out of the stadium and off the world —
  // the celebration turned a one-tick tunneling glitch into five seconds of
  // watching the ball fall into the void. A dead ball cannot continue a glitch.
  const b = state.ball;
  const gvx0 = b.vx, gvy0 = b.vy, gvz0 = b.vz;
  b.vx = 0; b.vy = 0; b.vz = 0;
  traceBallSince(state, BallVelocityCause.RuleRestart, -1, gvx0, gvy0, gvz0);
  b.wx = 0; b.wy = 0; b.wz = 0;
  b.possessedBy = -1;
  b.gatherOwner = -1; // §1: a frozen (goal) ball is nobody's to gather
}

// ---------------------------------------------------------------------------
// Input gating during restarts (§5.1)
// ---------------------------------------------------------------------------

/** The only verbs that "take" a restart — a pass or a shot from the spot. */
const TAKE_MASK = InputFlags.Pass | InputFlags.Shoot | InputFlags.ThroughBall;

/** Action bits that are illegal while the ball is not live. */
const RESTART_ACTION_MASK =
  TAKE_MASK |
  InputFlags.SlideTackle |
  InputFlags.StandingTackle |
  InputFlags.FirstTouch |
  InputFlags.GkDive |
  InputFlags.GkRush |
  InputFlags.GkPunch |
  // House rule: no decking opponents during a restart — no throwing hands at a
  // throw-in. Because these are not in TAKE_MASK, TAKER_ILLEGAL_MASK below picks
  // them up automatically, so the taker can't punch on the spot either.
  InputFlags.PunchPlayer |
  InputFlags.KickPlayer |
  // House rule: no jumping or bicycle-kicking while the ball is dead — leaping to
  // take a throw-in is nonsense, and a bicycle kick is a strike (also outside
  // TAKE_MASK, so the taker is denied it too). (2026-07-17)
  InputFlags.Jump |
  InputFlags.BicycleKick;

/**
 * Bits the taker still may NOT use — everything illegal except the take verbs.
 * The taker may play the ball, but must not tackle, dive or punch: a taker who
 * kept a tackle/punch flag would foul on the spot and re-trigger the restart in
 * a loop. (Punch/Kick are outside TAKE_MASK, so this mask includes them.)
 */
const TAKER_ILLEGAL_MASK = RESTART_ACTION_MASK & ~TAKE_MASK;

/** Whether an input carries a "take the restart" verb this tick. */
function wantsToPlay(input: InputCommand): boolean {
  return (input.flags & TAKE_MASK) !== 0;
}

/**
 * Neutralise illegal inputs for this tick. During a restart the only player who
 * may strike the ball is the designated taker, and only once we reach READY —
 * and even the taker may only PASS or SHOOT, never tackle. Movement, sprint and
 * jockey survive so players can still position.
 *
 * Mutating the passed InputCommand objects is intentional and deterministic:
 * step.ts hands these same objects to the player controller (stage 4) and kick
 * resolution (stage 6), so clearing the bits here is what actually gates them.
 * The gate is a pure function of (phase, stage, taker), so a re-simulated tick
 * gates identically — reconciliation is unaffected (§2.4).
 */
function gateInputs(
  state: WorldState,
  inputs: ReadonlyArray<InputCommand | null>,
  takerMayPlay: boolean,
): void {
  const p = state.players;
  const taker = state.match.restartTaker;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1) continue;
    const inp = inputs[i];
    if (inp === null || inp === undefined) continue;
    if (takerMayPlay && i === taker) {
      // The taker keeps the take verbs but loses tackles/dives and skills.
      inp.flags &= ~TAKER_ILLEGAL_MASK;
      inp.skill = SkillMove.None;
      continue;
    }
    inp.flags &= ~RESTART_ACTION_MASK;
    inp.skill = SkillMove.None;
    inp.passCharge = 0;
    inp.shootCharge = 0;
  }
}

// ---------------------------------------------------------------------------
// Kick-off legal formation (§5.1, Law 8) — playtest DEFECT #8
// ---------------------------------------------------------------------------

/**
 * ⚠ `pushOutsideRadius` — a POSITION shove that kept a player outside a keep-out
 * zone every tick — was DELETED here on 2026-08-09 (locomotion honesty).
 *
 * Its last caller was `enforceKickoffFormation`'s Law-8 clamp. Every other
 * restart had already migrated to `pushStageTargetOutsideRadius` below, which
 * moves the MARK instead of the man, and kick-off was the last holdout because
 * it was the last restart that teleported. A body-shoving clamp is exactly the
 * defect the owner reported, so it is gone rather than left as a decoy for the
 * next person adding a keep-out zone. Use the mark version.
 */

/**
 * The walk-in equivalent: keep player `i`'s staged MARK
 * at least `minDist` from (cx,cz) instead of shoving his body there.
 *
 * Composes on top of whatever role already claimed him this tick (see
 * stageBaseX) and is a no-op when the mark is already legal — so a defender
 * who was never inside the keep-out ring is left entirely alone, exactly as
 * the position version leaves him alone today. Applied LAST by every caller,
 * so the Law (13/15/17) distance is always the final authority over the
 * choreography's preferences.
 */
function pushStageTargetOutsideRadius(
  state: WorldState,
  i: number,
  cx: number,
  cz: number,
  minDist: number,
  fallbackDirX: number,
  fallbackDirZ: number,
): void {
  const bx = stageBaseX(state, i);
  const bz = stageBaseZ(state, i);
  const dx = bx - cx;
  const dz = bz - cz;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d >= minDist) return;
  // Same 1cm degeneracy guard as pushOutsideRadius: below that, treat the
  // direction as noise and use the caller's fixed fallback rather than scaling
  // a millimetre of jitter up into an oddly-aimed mark.
  if (d < 1e-2) {
    setStageTarget(i, cx + fallbackDirX * minDist, cz + fallbackDirZ * minDist);
  } else {
    const s = minDist / d;
    setStageTarget(i, cx + dx * s, cz + dz * s);
  }
}

/**
 * Law 8: at a kick-off every player is in their own half, and the team not
 * taking it stays outside the centre circle until the ball is live. Nothing
 * previously enforced this — individual.ts's decideRestart voluntarily seeks a
 * legal AI position (its own comment says so explicitly), but a human, or an
 * AI still mid-repath, could stand right on the ball. Run every Setup/Ready
 * tick so a violation never survives more than the tick it happened in.
 */
function enforceKickoffFormation(state: WorldState): void {
  const p = state.players;
  const m = state.match;
  // Formation-anchored MARKS first (the shape), then walk one capped step toward
  // them — the same mover every other restart uses. Without the shape, players
  // restarted from wherever the x=0 pin left them: a ragged halfway-line scatter
  // after every goal.
  computeStageTargets(state);
  applyStageTargets(state);

  // ── LAW 8 IS ENFORCED BY THE MARKS, NOT BY A CLAMP ON POSITION ────────────
  //
  // The old hard clamp — `if (px·dir > 0) px = 0` plus `pushOutsideRadius`,
  // every Setup and Ready tick — was only ever safe because the staging above
  // TELEPORTED everyone into a legal shape first, so it never actually fired.
  // With a walk-in it fires constantly, and MEASURED it became the new teleport:
  // a 3.86 m single-tick jump on two strikers at the whistle, replacing the
  // 40.24 m one it was supposed to remove. A Law is not enforced by teleporting
  // the offender.
  //
  // It is not needed, and that is provable rather than hopeful:
  //   1. EVERY kick-off mark is legal by construction. `stageKickoffShape` uses
  //      `fx = min(a.fx, KICKOFF_FORWARD_PULLBACK_FX) · KICKOFF_SHAPE_DEPTH`
  //      with KICKOFF_FORWARD_PULLBACK_FX = -0.06, so `px·dir = fx·halfL < 0`
  //      for every outfielder; the taker sits at `-dir·KICKOFF_TAKER_INSET`,
  //      also negative. The centre-circle stand-off is applied to the MARK
  //      (pushStageTargetOutsideRadius), same as every other restart.
  //   2. NOBODY CAN WANDER OFF A KICK-OFF MARK. `applyStageTargets` stage-locks
  //      every player it moves, and `runRestart`'s `spareHumans` excludes
  //      Kickoff — so the human is locked too and `stepPlayerController` never
  //      runs his locomotion. There is no mover left that could break Law 8.
  //   3. The whistle waits for the shape: `sizeSetupWindowForWalkIn` budgets the
  //      Setup window to the longest walk-in, so the formation is settled and
  //      exact before Ready.
  //
  // What remains is the §9 requirement that the ball goes live from a STILL
  // formation. `applyStageTargets` already zeroes velocity the moment a player
  // reaches his mark, so by the whistle that is true by construction — but a
  // player still closing the last millimetres when the window lapses would carry
  // a residual, so it is made explicit here from Ready onward.
  if (m.restartStage === RestartStage.Setup) return;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    const d = Math.hypot(stageTargetX[i]! - p.px[i]!, stageTargetZ[i]! - p.pz[i]!);
    if (stageHasTarget[i] === 1 && d > RESTART_STAGE_ON_MARK_DIST) continue; // still arriving
    p.vx[i] = 0; p.vy[i] = 0; p.vz[i] = 0;
  }
}

/**
 * Every player's kick-off MARK: his POSITION_ANCHORS home spot, compressed into
 * his own half. Duplicate positions (two CBs, two STs) split laterally; the
 * taker's mark is at the ball. Deterministic: pure scalar writes from static
 * roster data.
 *
 * ── This used to write POSITIONS, and that was the last teleport in the game ─
 *
 * Owner report, verbatim: *"make sure no player ever teleport or gets slided to
 * reach his posi"*. MEASURED (`locomotion-honesty.test.ts`, real 11v11):
 * at the Goal→Kickoff flip, **all 22 players jumped up to 40.24 m in ONE tick**
 * — 2414 m/s. It was the only true teleport left in the tape; ordinary in-play
 * locomotion never exceeded `MAX_SPEED_SPRINT × dt`.
 *
 * It is now a MARK writer like every other restart's staging function, so
 * `applyStageTargets` walks them in at the gait ladder. The shape it produces,
 * the anchors, the duplicate spread and the taker inset are all unchanged —
 * this changes how players arrive, not where.
 *
 * ⚠ The Law-8 clamps are NOT applied here. See `enforceKickoffFormation`: a
 * clamp on POSITION that fires mid-walk would pin everyone still in the wrong
 * half onto the halfway line every tick, which is the teleport again wearing a
 * law's name. Law 8 has to hold when the ball is KICKED, not while the shape is
 * still travelling.
 */
function stageKickoffShape(state: WorldState): void {
  const p = state.players;
  const m = state.match;
  const halfL = state.config.pitchLength * 0.5;
  const halfW = state.config.pitchWidth * 0.5;
  for (let team = 0; team < 2; team++) {
    const dir = attackDir(state, team);
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i] !== team) continue;
      const pos = state.positions[i]!;
      const a = POSITION_ANCHORS[pos];
      // Duplicate spread: rank this player among same-position teammates so
      // two CBs/STs split left-right instead of stacking on one spot.
      let nDup = 0;
      let rank = 0;
      for (let j = 0; j < MAX_PLAYERS; j++) {
        if (p.active[j] !== 1 || p.sentOff[j] === 1 || p.team[j] !== team) continue;
        if (state.positions[j] !== pos) continue;
        if (j < i) rank++;
        nDup++;
      }
      const spread = nDup > 1 ? (rank - (nDup - 1) * 0.5) * KICKOFF_DUP_SPREAD_FZ : 0;
      // Anchors ahead of KICKOFF_FORWARD_PULLBACK_FX (attackers) wait just
      // inside their own half at kick-off; everyone else keeps their depth.
      const fx = Math.min(a.fx, KICKOFF_FORWARD_PULLBACK_FX) * KICKOFF_SHAPE_DEPTH;
      setStageTarget(i, dir * fx * halfL, (a.fz + spread) * halfW * dir);
    }
  }
  // The taker stands at the ball, on his own side of it, ready to play.
  const taker = m.restartTaker;
  if (taker >= 0 && p.active[taker] === 1 && p.sentOff[taker] !== 1) {
    const dir = attackDir(state, p.team[taker]!);
    setStageTarget(taker, m.restartX - dir * KICKOFF_TAKER_INSET, m.restartZ);
  }

  // Law 8's centre-circle stand-off, applied to the MARK rather than to the
  // position — the same composition every other restart uses, where the Law
  // clamp adjusts the destination so a player walks to the legal spot instead of
  // being placed illegally and then shoved out of it.
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i] === m.restartTeam) continue; // the taking side may stand inside
    const dir = attackDir(state, p.team[i]!);
    pushStageTargetOutsideRadius(state, i, m.restartX, m.restartZ, KICKOFF_OPPONENT_DISTANCE, -dir, 0);
  }
}

/**
 * Law 13 staging (§5.3): free-kick defenders keep FREE_KICK_WALL_DISTANCE from
 * the ball, and when the kick genuinely threatens the goal a WALL forms — the
 * nearest 2-4 defending outfielders line up shoulder-to-shoulder on the
 * ball→goal line at exactly the 9.15m mark. Runs every Setup/Ready tick, so
 * neither drift nor the AI's own pathing can break the ceremony. Humans are
 * spared by runRestart's save/restore, same as every other staged restart.
 *
 * Writes MARKS, not positions (see the walk-in section at the top of the
 * file): the wall now forms by defenders jogging onto the line across the
 * Setup window instead of materialising on it in a single frame.
 */
function enforceFreeKickFormation(state: WorldState): void {
  const p = state.players;
  const m = state.match;
  const defendTeam = 1 - m.restartTeam;
  const goalX = defendGoalX(state, defendTeam);
  const dxGoal = goalX - m.restartX;
  const dzGoal = -m.restartZ; // goal centre is z=0
  const distToGoal = Math.sqrt(dxGoal * dxGoal + dzGoal * dzGoal);

  // Wall size by threat: close free kicks get a real wall, distant ones none.
  let wallSize = 0;
  if (distToGoal <= FREE_KICK_WALL_NEAR_DIST) wallSize = 4;
  else if (distToGoal <= FREE_KICK_WALL_MID_DIST) wallSize = 3;
  else if (distToGoal <= FREE_KICK_WALL_FAR_DIST) wallSize = 2;

  // Unit vector ball→goal and its perpendicular (the wall's facing line).
  const inv = distToGoal > 1e-6 ? 1 / distToGoal : 0;
  const ux = dxGoal * inv;
  const uz = dzGoal * inv;
  const baseX = m.restartX + ux * FREE_KICK_WALL_DISTANCE;
  const baseZ = m.restartZ + uz * FREE_KICK_WALL_DISTANCE;

  if (wallSize > 0) {
    // Fill the wall SLOT BY SLOT, each slot claiming the nearest defender to
    // ITS OWN mark. Selection is deterministic (pure float comparisons over a
    // fixed slot order, then a fixed player order).
    //
    // ⚠ This used to score every slot against the wall's CENTRE point and hand
    // out the shoulder-to-shoulder offsets in selection order, which was stable
    // only while staging TELEPORTED. Two defenders who end up equidistant from
    // the centre — which is precisely what standing in adjacent slots makes
    // them — swap selection order between ticks, and once they WALK to their
    // marks that swap becomes a permanent oscillation: each sets off toward the
    // other's mark, they cross, they swap back, 10 cm every tick, forever. The
    // wall never settles and two men vibrate in it. (Caught by
    // restart-walk-in.test.ts's settle assertion; MEASURED as players 18/19
    // trading (41.4,1.9)↔(41.4,2.0) for the entire Setup window.)
    //
    // Scoring a man against the slot he is actually walking to makes HIS
    // distance the one that shrinks fastest, so every tick re-elects the same
    // man — the same stable-fixed-point property fillRestartRoles relies on.
    const inWall = wallScratch;
    inWall.fill(0);
    for (let slot = 0; slot < wallSize; slot++) {
      // Shoulder-to-shoulder along the perpendicular, centred on the line.
      const off = (slot - (wallSize - 1) * 0.5) * FREE_KICK_WALL_SPACING;
      const [wx, wz] = clampToPitch(state, baseX + -uz * off, baseZ + ux * off);
      let best = -1;
      let bestSq = Number.POSITIVE_INFINITY;
      for (let i = 0; i < MAX_PLAYERS; i++) {
        if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
        if (p.team[i] !== defendTeam || inWall[i] === 1) continue;
        if (state.positions[i] === 'GK') continue;
        const dx = p.px[i]! - wx;
        const dz = p.pz[i]! - wz;
        const d = dx * dx + dz * dz;
        if (d < bestSq) {
          bestSq = d;
          best = i;
        }
      }
      if (best < 0) break;
      inWall[best] = 1;
      setStageTarget(best, wx, wz);
    }
  }

  // Law 13 distance for every other defender (wall members' marks already sit
  // at or beyond the limit, so the clamp is a no-op for them).
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i] !== defendTeam) continue;
    pushStageTargetOutsideRadius(state, i, m.restartX, m.restartZ, FREE_KICK_WALL_DISTANCE, ux, uz);
  }
}

/**
 * Law 14 staging (§5.3): the penalty ceremony. Defending keeper pinned to his
 * line; everyone except the taker and that keeper outside the penalty area,
 * outside the arc, and no closer to the goal than the spot; the taker on his
 * run-up mark. The generic Setup→Ready machine then plays the kick like any
 * restart — this function only shapes the scene it runs in.
 *
 * Writes MARKS, not positions (see the walk-in section at the top of the
 * file): the box empties by players WALKING out of it across the Setup
 * window, which is what a penalty looks like, instead of everyone inside it
 * blinking to the edge of the D in one frame.
 */
function enforcePenaltyFormation(state: WorldState): void {
  const p = state.players;
  const m = state.match;
  const defendTeam = 1 - m.restartTeam;
  const goalX = defendGoalX(state, defendTeam);
  const sign = goalX > 0 ? 1 : -1; // +1: goal at +halfL
  const spotX = m.restartX;
  const spotZ = m.restartZ;

  const gk = findGoalkeeperOfTeam(state, defendTeam);
  if (gk >= 0) {
    setStageTarget(gk, goalX - sign * 0.2, 0); // on his line, just inside the field
  }

  const taker = m.restartTaker;
  if (taker >= 0) {
    // On his run-up mark: behind the spot, toward midfield.
    setStageTarget(taker, spotX - sign * PENALTY_TAKER_RUNUP, spotZ);
  }

  // Everyone else: outside the area (with a margin), outside the arc, and not
  // between the spot and the goal line. Box clamp first, then the arc push —
  // the push's radial direction from the spot points toward midfield for
  // anyone already clamped to the box edge, so it cannot shove them back in.
  //
  // Both clamps are evaluated against the player's CURRENT position, which is
  // exactly what keeps his mark stable while he walks: he reads as "inside the
  // box" (mark = the box edge) every tick until he actually crosses it, and on
  // the tick he does, the mark becomes where he stands and he stops there.
  const boxEdgeX = goalX - sign * (PENALTY_AREA_LENGTH + PENALTY_WAITER_MARGIN);
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (i === gk || i === taker) continue;
    let tx = p.px[i]!;
    const tz = p.pz[i]!;
    const insideBox =
      Math.abs(p.px[i]! - goalX) <= PENALTY_AREA_LENGTH + PENALTY_WAITER_MARGIN &&
      Math.abs(p.pz[i]!) <= PENALTY_AREA_WIDTH * 0.5 + PENALTY_WAITER_MARGIN;
    if (insideBox) tx = boxEdgeX;
    // Goal side of the spot but outside the box laterally → still pull them
    // level with the spot (Law 14: no one nearer the goal line than the ball).
    if (sign > 0 ? p.px[i]! > spotX : p.px[i]! < spotX) {
      if (Math.abs(p.pz[i]!) > PENALTY_AREA_WIDTH * 0.5) tx = spotX;
    }
    setStageTarget(i, tx, tz);
    pushStageTargetOutsideRadius(state, i, spotX, spotZ, PENALTY_ARC_RADIUS, -sign, 0);
  }
}

/**
 * Law 16 staging: at a goal kick every opponent stays outside the penalty
 * area until the ball is in play. Own team unrestricted.
 *
 * Writes MARKS, not positions — an opponent caught in the box walks out of it
 * rather than being flicked to the edge (see the walk-in section at the top).
 */
function enforceGoalKickFormation(state: WorldState): void {
  const p = state.players;
  const m = state.match;
  const goalX = defendGoalX(state, m.restartTeam);
  const sign = goalX > 0 ? 1 : -1;
  const boxEdgeX = goalX - sign * (PENALTY_AREA_LENGTH + PENALTY_WAITER_MARGIN);
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i] === m.restartTeam) continue;
    const insideBox =
      Math.abs(p.px[i]! - goalX) <= PENALTY_AREA_LENGTH + PENALTY_WAITER_MARGIN &&
      Math.abs(p.pz[i]!) <= PENALTY_AREA_WIDTH * 0.5 + PENALTY_WAITER_MARGIN;
    // Same stability property as the penalty box clamp: he reads as inside the
    // box (mark = the edge) every tick until he actually walks out of it.
    if (insideBox) setStageTarget(i, boxEdgeX, p.pz[i]!);
  }
}

// Scratch for the free-kick wall membership (allocation-free per tick).
const wallScratch = new Uint8Array(MAX_PLAYERS);

/**
 * Law 15: opponents keep THROW_OPPONENT_DISTANCE from the point of the throw
 * until it is released. The thrower's own team is unrestricted — a teammate
 * may stand anywhere legal, same as at a kick-off.
 *
 * Adjusts MARKS, not positions — the opponent RETREATS the 2m rather than
 * popping back (see the walk-in section at the top of the file).
 */
function enforceThrowInDistance(state: WorldState): void {
  const p = state.players;
  const m = state.match;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! === m.restartTeam) continue;
    const dir = attackDir(state, p.team[i]!);
    pushStageTargetOutsideRadius(state, i, m.restartX, m.restartZ, THROW_OPPONENT_DISTANCE, -dir, 0);
  }
}

// ---------------------------------------------------------------------------
// Contextual restart choreography — corners & throw-ins (§5.2, Law 15/17).
//
// Before this, corners and throw-ins were "generic restarts": the taker and
// the required opponent distance were handled, but every OTHER player stood
// wherever open play had left them — no roles, no adaptation to which side
// of the pitch the ball went out, the same shape whether it was a corner on
// the left or the right. This section adds a deterministic, contextual
// choreography on top of the existing staging mechanism (the same
// "position-correction, self-healing every tick" pattern
// enforceKickoffFormation/enforceThrowInDistance already use — no second
// mover is introduced).
//
// The selector (fillRestartRoles) is one greedy pass: for each role in
// PRIORITY order, score every not-yet-placed candidate and send the best one
// to that role's target. Never random, never a single fixed formation — the
// role TARGETS adapt to the restart's field location (corner side / throw
// spot) and to score+time (see isLosingLateForExtraAttacker); the role
// FILLERS adapt to attributes (heading+jumping for aerial roles — there is no
// literal "height" attribute on Attributes, so jumping is the sim's real
// proxy for aerial reach, exactly as headers and GK diving already use it),
// outfield position (attackMindedBias) and proximity. Because attributes and
// position never change tick to tick, and distance is only a small
// tie-breaker, one assignment is a stable fixed point across the whole
// Setup/Ready window rather than reshuffling every tick.
// ---------------------------------------------------------------------------

/** Clamp a target to just inside the pitch so a role offset can never park a
 *  player in the advertising boards, regardless of squad-size pitch scale. */
function clampToPitch(state: WorldState, x: number, z: number): readonly [number, number] {
  const halfLength = state.config.pitchLength * 0.5 - 0.5;
  const halfWidth = state.config.pitchWidth * 0.5 - 0.5;
  return [clamp(x, -halfLength, halfLength), clamp(z, -halfWidth, halfWidth)];
}

/**
 * Outfield-position bias toward attacking, [0,1]. Ties a support-role fit to
 * WHO the player nominally is (§8.2) rather than only proximity/attributes.
 * Neutral (0.45) for anything not explicitly forward/defensive-shaped so a
 * squad of generic positions (most tests spawn everyone 'CM') still degrades
 * to a sane, attribute/distance-driven assignment.
 */
function attackMindedBias(pos: Position): number {
  switch (pos) {
    case 'ST':
    case 'CF':
      return 1.0;
    case 'LW':
    case 'RW':
      return 0.85;
    case 'CAM':
      return 0.75;
    case 'LM':
    case 'RM':
      return 0.55;
    case 'CDM':
      return 0.3;
    case 'LWB':
    case 'RWB':
      return 0.35;
    case 'LB':
    case 'RB':
      return 0.2;
    case 'CB':
      return 0.05;
    case 'GK':
      return 0; // never actually scored — GKs are excluded from the candidate pool
    default:
      return 0.45;
  }
}

/**
 * Score candidate `i` for a role targeting (tx,tz). Aerial roles are decided
 * by heading+jumping; everything else leans on outfield position. Distance is
 * a small negative tie-breaker only — see the section header for why it must
 * stay small relative to the other terms.
 */
function scoreForRestartRole(state: WorldState, i: number, tx: number, tz: number, aerial: boolean): number {
  const p = state.players;
  const dx = p.px[i]! - tx;
  const dz = p.pz[i]! - tz;
  const dist = Math.sqrt(dx * dx + dz * dz);
  let score = -dist * RESTART_ROLE_DISTANCE_WEIGHT;
  if (aerial) {
    const a = state.attributes[i]!;
    score += (a.heading + a.jumping) * RESTART_ROLE_AERIAL_WEIGHT;
  } else {
    score += attackMindedBias(state.positions[i]!) * RESTART_ROLE_POSITION_WEIGHT;
  }
  return score;
}

/** Active, not-sent-off outfield (non-GK) players of `team`, excluding `exclude`. */
function activeOutfieldCandidates(state: WorldState, team: number, exclude: number): number[] {
  const p = state.players;
  const out: number[] = [];
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! !== team) continue;
    if (i === exclude) continue;
    if (state.positions[i] === 'GK') continue;
    out.push(i);
  }
  return out;
}

/**
 * One staged role: [targetX, targetZ, scoredOnAerialAttributes, isCeremonyCritical].
 * `isCeremonyCritical` defaults to true when omitted — see stageTargetCritical.
 */
type RestartRole = readonly [number, number, boolean, boolean?];

/**
 * Greedily fill `roles` (priority order) from `candidates`: for each role,
 * take the best-scoring not-yet-taken candidate and give him that role's MARK
 * to walk to (applyStageTargets does the walking). A role goes unfilled — not
 * crashed — if the squad runs out of candidates (small squad sizes).
 *
 * The assignment stays a stable fixed point across the walk-in for the same
 * reason it was stable back when staging snapped: attributes and outfield
 * position never change tick to tick, and the only term that does — distance
 * to the role's target — shrinks FASTEST for the man already walking to it, so
 * each tick's greedy pass re-elects the same player.
 */
function fillRestartRoles(
  state: WorldState,
  candidates: readonly number[],
  taken: Uint8Array,
  roles: ReadonlyArray<RestartRole>,
): void {
  for (const [tx, tz, aerial, critical] of roles) {
    let best = -1;
    let bestScore = Number.NEGATIVE_INFINITY;
    for (const i of candidates) {
      if (taken[i]) continue;
      const s = scoreForRestartRole(state, i, tx, tz, aerial);
      if (s > bestScore) {
        bestScore = s;
        best = i;
      }
    }
    if (best < 0) continue;
    taken[best] = 1;
    setStageTarget(best, tx, tz, critical !== false);
  }
}

/**
 * Whatever candidates a named-role pass left over get sent back toward their
 * own half — "rest defend"/"rest defence" — rather than left standing
 * wherever open play parked them. Spread laterally (alternating sides,
 * increasing rank) so they do not stack on one spot.
 */
function sendToRestDefend(state: WorldState, candidates: readonly number[], taken: Uint8Array, dir: number): void {
  let idx = 0;
  for (const i of candidates) {
    if (taken[i]) continue;
    const sideSign = idx % 2 === 0 ? 1 : -1;
    const rank = Math.floor(idx / 2) + 1;
    const [x, z] = clampToPitch(
      state,
      -dir * RESTART_REST_DEFEND_OWN_HALF_INSET,
      sideSign * rank * RESTART_REST_DEFEND_SPREAD_Z,
    );
    // Non-critical: nobody waits for rest defence to reach the halfway line.
    setStageTarget(i, x, z, false);
    taken[i] = 1;
    idx++;
  }
}

/**
 * Score+time context (§10.2-style decision weight, not a stat boost): losing,
 * in the second half, inside the last CORNER_LATE_GAME_REMAINING_FRAC of the
 * half (added time included) → this corner sends an extra man forward.
 */
function isLosingLateForExtraAttacker(state: WorldState, team: number): boolean {
  const m = state.match;
  if (m.half < 2) return false;
  const halfTicks = Math.round(state.config.halfLengthS * SIMULATION_TICK_RATE) + m.addedTimeTicks;
  if (halfTicks <= 0) return false;
  const remainingFrac = 1 - m.clockTicks / halfTicks;
  if (remainingFrac > CORNER_LATE_GAME_REMAINING_FRAC) return false;
  const myScore = team === 0 ? m.scoreA : m.scoreB;
  const oppScore = team === 0 ? m.scoreB : m.scoreA;
  return myScore < oppScore;
}

/** The defending team's goalkeeper, or -1 if none is tagged (small squads). */
function findGoalkeeperOfTeam(state: WorldState, team: number): number {
  const p = state.players;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! !== team) continue;
    if (state.positions[i] === 'GK') return i;
  }
  return -1;
}

/**
 * Corner-kick contextual choreography (Law 17; playtest §8). Attacking roles:
 * taker, short option, near-post runner, central target, far-post runner,
 * edge-of-box option, rebound player, rest defend. Defending roles: near-post
 * defender, central markers, far-post defender, GK positioned, edge-of-box
 * defender, counter outlet. Runs every Setup/Ready tick, same as
 * enforceKickoffFormation — see the section header for the selector.
 *
 * Writes MARKS, not positions (see the walk-in section at the top of the
 * file): the taker JOGS TO THE FLAG and the box fills up as runners arrive,
 * rather than the whole scene cutting into place on the first Setup tick.
 */
function enforceCornerFormation(state: WorldState): void {
  const m = state.match;
  const attackTeam = m.restartTeam;
  const defendTeam = 1 - attackTeam;
  const dir = attackDir(state, attackTeam);
  const side = m.restartZ >= 0 ? 1 : -1; // which touchline the corner was won on
  const goalLineX = m.restartX;

  // The taker walks to just infield of the arc — within kick reach, not on the flag.
  const taker = m.restartTaker;
  if (taker >= 0) {
    const [tx, tz] = clampToPitch(
      state,
      goalLineX - dir * CORNER_TAKER_INSET,
      m.restartZ - side * CORNER_TAKER_INSET,
    );
    setStageTarget(taker, tx, tz);
  }

  // ── Attacking team: named roles, priority order, then rest defend. ───────
  const [shortOptX, shortOptZ] = clampToPitch(
    state, goalLineX - dir * CORNER_SHORT_OPTION_DEPTH, m.restartZ - side * CORNER_SHORT_OPTION_WIDTH_INSET,
  );
  const [nearPostX, nearPostZ] = clampToPitch(state, goalLineX - dir * CORNER_NEAR_POST_DEPTH, side * CORNER_NEAR_POST_Z);
  const [centralX, centralZ] = clampToPitch(state, goalLineX - dir * CORNER_CENTRAL_TARGET_DEPTH, 0);
  const [farPostX, farPostZ] = clampToPitch(state, goalLineX - dir * CORNER_FAR_POST_DEPTH, -side * CORNER_FAR_POST_Z);
  const [edgeBoxX, edgeBoxZ] = clampToPitch(state, goalLineX - dir * PENALTY_AREA_LENGTH, 0);
  const [reboundX, reboundZ] = clampToPitch(state, goalLineX - dir * CORNER_REBOUND_DEPTH, side * CORNER_REBOUND_Z);

  const attackRoles: RestartRole[] = [
    // Non-critical for the same reason as the throw-in receivers: Law 17
    // constrains the DEFENDERS (9.15 m off the arc), not where the attackers
    // stand. Attackers arriving late into the box is what a corner IS — a
    // static, fully-arrived box is the thing that reads as dead. MEASURED: the
    // corner's setup window was pinned at its 17.5 s CAP by these runs.
    [nearPostX, nearPostZ, true, false],
    [centralX, centralZ, true, false],
    [farPostX, farPostZ, true, false],
    [shortOptX, shortOptZ, false, false],
    [edgeBoxX, edgeBoxZ, false, false],
    [reboundX, reboundZ, false, false],
  ];
  if (isLosingLateForExtraAttacker(state, attackTeam)) {
    // "Losing late → extra man forward": one more aerial body in the middle,
    // taken from what would otherwise be a rest-defend slot.
    const [exX, exZ] = clampToPitch(state, goalLineX - dir * CORNER_CENTRAL_TARGET_DEPTH, -side * CORNER_NEAR_POST_Z);
    attackRoles.push([exX, exZ, true, false]);
  }

  const attackCandidates = activeOutfieldCandidates(state, attackTeam, taker);
  const attackTaken = new Uint8Array(MAX_PLAYERS);
  fillRestartRoles(state, attackCandidates, attackTaken, attackRoles);
  sendToRestDefend(state, attackCandidates, attackTaken, dir);

  // ── Defending team: GK positioned, then named roles, then central markers
  //    (mark the actual attacking targets just assigned — "opponent
  //    positions: mark the actual runners"), then overflow cover. ──────────
  const defendGk = findGoalkeeperOfTeam(state, defendTeam);
  if (defendGk >= 0) {
    const [gx, gz] = clampToPitch(state, goalLineX - dir * CORNER_GK_LINE_INSET, side * CORNER_GK_NEAR_POST_BIAS);
    setStageTarget(defendGk, gx, gz);
  }

  const [npDefX, npDefZ] = clampToPitch(state, goalLineX - dir * CORNER_POST_DEFENDER_INSET, side * CORNER_NEAR_POST_Z);
  const [fpDefX, fpDefZ] = clampToPitch(state, goalLineX - dir * CORNER_POST_DEFENDER_INSET, -side * CORNER_FAR_POST_Z);
  const [outletX, outletZ] = clampToPitch(state, -dir * CORNER_COUNTER_OUTLET_X, 0);
  // Goal-side of the attacker being marked: shifted further toward the goal
  // line than the attacker's own spot (dir points center→goal-line), so a
  // marker never lands exactly on top of the attacker it is marking.
  const [ebDefX, ebDefZ] = clampToPitch(state, edgeBoxX + dir * AI_MARK_GOALSIDE_DISTANCE, edgeBoxZ);
  const [cm1X, cm1Z] = clampToPitch(state, centralX + dir * AI_MARK_GOALSIDE_DISTANCE, centralZ);
  const [cm2X, cm2Z] = clampToPitch(state, reboundX + dir * AI_MARK_GOALSIDE_DISTANCE, reboundZ);
  const [cm3X, cm3Z] = clampToPitch(state, shortOptX + dir * AI_MARK_GOALSIDE_DISTANCE, shortOptZ);

  // Defensive shape at a corner is also NOT ceremony-critical (2026-08-09,
  // dwell). Law 17's only positional requirement is the 9.15 m stand-off from
  // the arc, and pushStageTargetOutsideRadius holds that every tick wherever a
  // defender happens to be — it is not something anyone has to arrive at. Post
  // defenders and markers jogging into the six-yard box as the ball comes in is
  // ordinary football; making the referee wait for them cost the corner its
  // entire 17.5 s cap. See maxStageTravel.
  const defendRoles: ReadonlyArray<RestartRole> = [
    [npDefX, npDefZ, true, false],
    [fpDefX, fpDefZ, true, false],
    [ebDefX, ebDefZ, false, false],
    // The counter outlet is the length of the pitch from the corner flag and,
    // like rest defence, is somebody jogging upfield as the kick is taken —
    // NOT ceremony-critical (see stageTargetCritical).
    [outletX, outletZ, false, false],
    [cm1X, cm1Z, true, false],
    [cm2X, cm2Z, false, false],
    [cm3X, cm3Z, false, false],
  ];
  const defendCandidates = activeOutfieldCandidates(state, defendTeam, -1);
  const defendTaken = new Uint8Array(MAX_PLAYERS);
  fillRestartRoles(state, defendCandidates, defendTaken, defendRoles);

  // Overflow central markers (bigger squads than named marks) get generic
  // deep cover instead of crowding the zones already marked above.
  let overflowIdx = 0;
  for (const i of defendCandidates) {
    if (defendTaken[i]) continue;
    const sideSign = overflowIdx % 2 === 0 ? 1 : -1;
    const rank = Math.floor(overflowIdx / 2) + 1;
    const [ox, oz] = clampToPitch(
      state, goalLineX - dir * CORNER_MARKER_COVER_DEPTH, sideSign * rank * CORNER_MARKER_COVER_SPREAD_Z,
    );
    setStageTarget(i, ox, oz);
    defendTaken[i] = 1;
    overflowIdx++;
  }

  // Law 17: no defender — whatever role sent them where they are — may be
  // inside CORNER_OPPONENT_DISTANCE of the arc until it is live. A corner's
  // short option deliberately stands close; nobody may mark him that tightly
  // before the kick.
  for (const i of defendCandidates) {
    pushStageTargetOutsideRadius(state, i, m.restartX, m.restartZ, CORNER_OPPONENT_DISTANCE, -dir, 0);
  }
  if (defendGk >= 0) {
    pushStageTargetOutsideRadius(state, defendGk, m.restartX, m.restartZ, CORNER_OPPONENT_DISTANCE, -dir, 0);
  }
}

/**
 * Throw-in contextual choreography (Law 15; playtest §8). Attacking roles:
 * taker, short receiver, line runner, inside support, backward safety, rest
 * defence. Defending roles: nearby markers (shadow the two furthest-forward
 * options), rest defence (the existing THROW_OPPONENT_DISTANCE hold IS their
 * job — holding the required distance is what "rest defence" means here).
 *
 * Writes MARKS, not positions (see the walk-in section at the top of the
 * file): the thrower WALKS to the touchline to collect the ball.
 */
function enforceThrowInFormation(state: WorldState): void {
  const m = state.match;
  const team = m.restartTeam;
  const defendTeam = 1 - team;
  const dir = attackDir(state, team);
  const side = m.restartZ >= 0 ? 1 : -1;
  const p = state.players;

  // Inset off the exact spot — coinciding with the ball's resting position
  // would overlap the taker's capsule with it, and the physics step's contact
  // resolution would shove the "dead" ball off-spot with residual velocity
  // (measured: the staging-dwell ball drifted ~1.2cm/tick). Still comfortably
  // inside THROW_TAKE_REACH so an early release still works.
  const taker = m.restartTaker;
  if (taker >= 0) {
    const [tkx, tkz] = clampToPitch(state, m.restartX - dir * THROW_TAKER_INSET, m.restartZ - side * THROW_TAKER_INSET);
    setStageTarget(taker, tkx, tkz);
  }

  const [shortX, shortZ] = clampToPitch(
    state, m.restartX + dir * THROW_SHORT_RECEIVER_DEPTH, m.restartZ - side * THROW_SHORT_RECEIVER_WIDTH_INSET,
  );
  const [lineX, lineZ] = clampToPitch(state, m.restartX + dir * THROW_LINE_RUNNER_DEPTH, m.restartZ);
  const [insideX, insideZ] = clampToPitch(
    state, m.restartX + dir * THROW_INSIDE_SUPPORT_DEPTH, m.restartZ - side * THROW_INSIDE_SUPPORT_WIDTH_INSET,
  );
  const [backX, backZ] = clampToPitch(
    state, m.restartX - dir * THROW_BACKWARD_SAFETY_DEPTH, m.restartZ - side * THROW_BACKWARD_SAFETY_WIDTH_INSET,
  );

  // ── NOT CEREMONY-CRITICAL (2026-08-09, dwell) ─────────────────────────────
  // These four are RECEIVERS. Nothing in Law 15 requires them to be anywhere;
  // the only legal constraint at a throw-in is that OPPONENTS stand off, and
  // enforceThrowInDistance owns that separately.
  //
  // They used to be critical, which meant the referee's whistle waited for the
  // last of them to finish walking. MEASURED across three seeds: the taker was
  // stood over the ball with the ball in his hands for 3.4 s (near throw) to
  // 9.4 s (far throw) before the whistle went — pure dead air, and the single
  // largest block of it in the whole ceremony. A receiver still jogging into
  // space as the throw is taken is not a defect; it is what a throw-in looks
  // like. They keep their marks and keep walking to them; they just no longer
  // hold up the restart.
  const roles: ReadonlyArray<RestartRole> = [
    [shortX, shortZ, false, false],
    [lineX, lineZ, false, false],
    [insideX, insideZ, false, false],
    [backX, backZ, false, false],
  ];
  const candidates = activeOutfieldCandidates(state, team, taker);
  const taken = new Uint8Array(MAX_PLAYERS);
  fillRestartRoles(state, candidates, taken, roles);
  sendToRestDefend(state, candidates, taken, dir);

  // "Nearby markers": shadow the two furthest-forward options tightly.
  // Everyone else on the defending team is left to enforceThrowInDistance
  // (called right after this) — holding the required Law 15 distance IS
  // their "rest defence".
  const defendCandidates = activeOutfieldCandidates(state, defendTeam, -1);
  const markTargets: ReadonlyArray<readonly [number, number]> = [[shortX, shortZ], [lineX, lineZ]];
  const markTaken = new Uint8Array(MAX_PLAYERS);
  for (const [mx, mz] of markTargets) {
    let best = -1;
    let bestDsq = Number.POSITIVE_INFINITY;
    for (const i of defendCandidates) {
      if (markTaken[i]) continue;
      const dx = p.px[i]! - mx;
      const dz = p.pz[i]! - mz;
      const dsq = dx * dx + dz * dz;
      if (dsq < bestDsq) {
        bestDsq = dsq;
        best = i;
      }
    }
    if (best < 0) continue;
    markTaken[best] = 1;
    const [mkx, mkz] = clampToPitch(state, mx - dir * AI_MARK_GOALSIDE_DISTANCE, mz);
    // Not ceremony-critical: marking a run is shape, not a Law 15 requirement
    // (enforceThrowInDistance owns the only distance the law cares about, and
    // owns it as a per-tick push rather than an arrival). See maxStageTravel.
    setStageTarget(best, mkx, mkz, false);
  }
}

// ---------------------------------------------------------------------------
// Throw-in execution (§5.2, Law 15) — playtest DEFECT #7
// ---------------------------------------------------------------------------

/**
 * Nearest teammate of `thrower` satisfying the filters, or -1. In-field is
 * always required; `requireUnmarked`/`requireForward` are relaxed in turn by
 * `selectThrowTarget` so a throw always finds someone when the pitch has
 * anyone on it at all — no possession-model scoring (a later phase).
 */
function nearestTeammateMatching(
  state: WorldState,
  thrower: number,
  team: number,
  dir: number,
  requireUnmarked: boolean,
  requireForward: boolean,
): number {
  const p = state.players;
  const halfLength = state.config.pitchLength * 0.5;
  const halfWidth = state.config.pitchWidth * 0.5;
  let best = -1;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === thrower || p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! !== team) continue;
    if (Math.abs(p.px[i]!) > halfLength || Math.abs(p.pz[i]!) > halfWidth) continue; // in-field only
    const dx = p.px[i]! - p.px[thrower]!;
    const dz = p.pz[i]! - p.pz[thrower]!;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < 1e-3 || dist > THROW_TARGET_MAX_RANGE) continue;
    if (requireForward && dx * dir < -THROW_FORWARD_SLACK) continue;
    if (requireUnmarked) {
      let nearestOpp = Number.POSITIVE_INFINITY;
      for (let j = 0; j < MAX_PLAYERS; j++) {
        if (p.active[j] !== 1 || p.sentOff[j] === 1 || p.team[j]! === team) continue;
        const odx = p.px[i]! - p.px[j]!;
        const odz = p.pz[i]! - p.pz[j]!;
        const od = Math.sqrt(odx * odx + odz * odz);
        if (od < nearestOpp) nearestOpp = od;
      }
      if (nearestOpp < THROW_MARK_TIGHT_DIST) continue;
    }
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

/**
 * Simple, deterministic throw target: the nearest teammate who is in-field,
 * not tightly marked, and roughly toward the attacking goal — each filter
 * relaxed in turn so a throw always finds someone when the squad allows it.
 * Deliberately no possession-model scoring (that is a later phase, per the
 * authorization for this one).
 */
function selectThrowTarget(state: WorldState, thrower: number): number {
  const team = state.players.team[thrower]!;
  const dir = attackDir(state, team);
  let target = nearestTeammateMatching(state, thrower, team, dir, true, true);
  if (target < 0) target = nearestTeammateMatching(state, thrower, team, dir, false, true);
  if (target < 0) target = nearestTeammateMatching(state, thrower, team, dir, false, false);
  return target;
}

/**
 * Execute the throw: a real, traced impulse (never a foot strike), aimed at
 * `selectThrowTarget`'s pick. Speed and launch angle both ride the same
 * distance fraction — short throws are flat lobs at THROW_MIN_SPEED, long
 * ones ride THROW_MAX_SPEED with the extra THROW_ANGLE_LIFT_DEG of arc.
 * Ball becomes live on release: possession clears, lastTouch is stamped so
 * resolveKicks (stage 6, later this same tick) does not also try to strike it.
 */
function executeThrowIn(state: WorldState, thrower: number): void {
  const b = state.ball;
  const p = state.players;
  const team = p.team[thrower]!;
  const m = state.match;

  const targetId = selectThrowTarget(state, thrower);
  let dx: number;
  let dz: number;
  if (targetId >= 0) {
    dx = p.px[targetId]! - b.px;
    dz = p.pz[targetId]! - b.pz;
  } else {
    // No legal teammate at all (degenerate squad sizes) — throw infield rather
    // than stand there forever; the centre spot is always in bounds.
    dx = -b.px;
    dz = -b.pz;
  }
  let dist = Math.sqrt(dx * dx + dz * dz);
  if (dist < 1e-3) {
    dx = attackDir(state, team);
    dz = 0;
    dist = 1;
  }
  const invDist = 1 / dist;
  const dirx = dx * invDist;
  const dirz = dz * invDist;

  const distClamped = clamp(dist, THROW_SPEED_RANGE_MIN_DIST, THROW_SPEED_RANGE_MAX_DIST);
  const frac =
    (distClamped - THROW_SPEED_RANGE_MIN_DIST) / (THROW_SPEED_RANGE_MAX_DIST - THROW_SPEED_RANGE_MIN_DIST);
  const speed = THROW_MIN_SPEED + frac * (THROW_MAX_SPEED - THROW_MIN_SPEED);
  const angleRad = (THROW_ANGLE_DEG + frac * THROW_ANGLE_LIFT_DEG) * DEG2RAD;
  const cosA = dcos(angleRad);
  const sinA = dsin(angleRad);

  const rvx0 = b.vx, rvy0 = b.vy, rvz0 = b.vz;
  b.py = THROW_RELEASE_HEIGHT;
  b.vx = dirx * speed * cosA;
  b.vz = dirz * speed * cosA;
  b.vy = speed * sinA;
  b.wx = 0; b.wy = 0; b.wz = 0; // a two-handed throw carries no meaningful spin
  b.grounded = 0;
  b.possessedBy = -1;
  b.gatherOwner = -1; // §1: a thrown-in ball is played (like a kick) — not the thrower's to gather back
  b.lastTouchedBy = thrower;
  b.lastTouchedTeam = team;
  b.lastTouchTick = state.tick; // guards resolveKicks from striking it again this tick
  traceBallSince(state, BallVelocityCause.RuleRestart, thrower, rvx0, rvy0, rvz0);

  // THE RELEASE. Without this the ball simply departs on its own: the thrower
  // holds AnimState.ThrowInHold through the Ready window, the ball leaves, and
  // he drops straight back to the locomotion ladder having never thrown
  // anything. Verified in a live throw-in (match phase 4, 70 samples): hold
  // played, `AnimState.Throw` NEVER fired once — nothing anywhere in the sim
  // assigned it, despite `THROW_ANIM_HOLD_S` existing to time it and
  // the renderer documenting this function as the thing that stamps it.
  m.phase = MatchPhase.InPlay;
  m.restartStage = RestartStage.Live;
  m.stageTimer = 0;
  m.possessionTeam = team;
}

/**
 * Is `taker` close enough to the spot to have his hands on the ball?
 *
 * The gate on every ball-to-hands write below. Without it the ball snapped to
 * head height in front of a man who was still twenty metres up the touchline —
 * the ball led the fetch instead of waiting for it.
 */
function takerHasArrived(state: WorldState, taker: number): boolean {
  if (taker < 0) return false;
  const p = state.players;
  if (p.active[taker] !== 1 || p.sentOff[taker] === 1) return false;
  const dx = p.px[taker]! - state.match.restartX;
  const dz = p.pz[taker]! - state.match.restartZ;
  return dx * dx + dz * dz <= THROW_TAKE_REACH * THROW_TAKE_REACH;
}

/** Smoothstep. Kills the velocity discontinuity at both ends of a lift. */
function smoothstep01(t: number): number {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
}

/**
 * Put the dead ball where the taker's hands are at fraction `raise` of the lift.
 *
 * `raise` 0 = resting on the turf ON THE SPOT (his hands are down at it);
 * `raise` 1 = the Law 15 overhead release point, just behind the head.
 * In between it travels through the two-handed CARRY — chest height, in front
 * of him — so the arc is turf → chest → over the head, which is the order a
 * human picks a ball up and throws it in.
 *
 * Interpolating through the carry rather than straight from turf to overhead
 * matters: a straight line would take the ball diagonally THROUGH the taker's
 * capsule (the spot is behind him once he is on his inset mark, the release
 * point is behind his head), which is the "ball intersecting the player" defect.
 */
function placeThrowInBall(state: WorldState, thrower: number, raise: number): void {
  const p = state.players;
  const b = state.ball;
  const m = state.match;
  const yaw = p.yaw[thrower]!;
  const cy = dcos(yaw);
  const sy = dsin(yaw);
  const t = smoothstep01(raise);

  // Leg 1 (t: 0 → 0.5)  turf-on-the-spot → carry at the chest, in front.
  // Leg 2 (t: 0.5 → 1)  carry → straight UP → back over the head.
  const carryX = p.px[thrower]! + cy * THROW_CARRY_FRONT_OFFSET;
  const carryZ = p.pz[thrower]! + sy * THROW_CARRY_FRONT_OFFSET;
  const relX = p.px[thrower]! - cy * THROW_HANDS_BACK_OFFSET;
  const relZ = p.pz[thrower]! - sy * THROW_HANDS_BACK_OFFSET;

  if (t <= 0.5) {
    const u = t * 2;
    b.px = m.restartX + (carryX - m.restartX) * u;
    b.pz = m.restartZ + (carryZ - m.restartZ) * u;
    b.py = BALL_RADIUS + (THROW_CARRY_HEIGHT - BALL_RADIUS) * u;
  } else {
    const u = (t - 0.5) * 2;
    if (u <= THROW_WINDUP_RAISE_FRAC) {
      // Up, still out in front — clear of his own capsule the whole way.
      b.px = carryX;
      b.pz = carryZ;
      b.py = THROW_CARRY_HEIGHT
        + (THROW_RELEASE_HEIGHT - THROW_CARRY_HEIGHT) * (u / THROW_WINDUP_RAISE_FRAC);
    } else {
      // …then back over the head, at a height that clears the capsule top.
      const v = (u - THROW_WINDUP_RAISE_FRAC) / (1 - THROW_WINDUP_RAISE_FRAC);
      b.px = carryX + (relX - carryX) * v;
      b.pz = carryZ + (relZ - carryZ) * v;
      b.py = THROW_RELEASE_HEIGHT;
    }
  }

  b.vx = 0; b.vy = 0; b.vz = 0;
  b.wx = 0; b.wy = 0; b.wz = 0;
  b.grounded = raise <= 0 ? 1 : 0;
  b.possessedBy = -1;
  b.gatherOwner = -1;
}

/** Keep the dead ball in the two-handed carry while he waits for the whistle. */
function attachThrowInBallToHands(state: WorldState, thrower: number): void {
  placeThrowInBall(state, thrower, 0.5);
}

/**
 * How far through the pickup he is, 0..1, at this point in the Ready window.
 *
 * The lift runs over the FIRST THROW_PICKUP_S of Ready — after the whistle, see
 * THROW_PICKUP_S's own note. Derived from `stageTimer` counting down from
 * RESTART_READY_S, so there is no pickup timer to clone, hash or serialise.
 */
function throwInPickupFraction(state: WorldState): number {
  const elapsed = RESTART_READY_S - state.match.stageTimer;
  return clamp(elapsed / THROW_PICKUP_S, 0, 1);
}

/** Start the authored release; detachment waits for its contact pose. */
function armThrowInRelease(state: WorldState, thrower: number): void {
  const p = state.players;
  // The Law 15 release is an ACCEPTED action (§11) — unconditional, so a second
  // throw-in taken from the same spot is a second execution.
  beginAnimAction(p, thrower, AnimState.Throw);
  p.actionTimer[thrower] = THROW_ANIM_HOLD_S;
  state.match.stageTimer = THROW_RELEASE_CONTACT_S;
  attachThrowInBallToHands(state, thrower);
}

/**
 * The Ready stage of a throw-in: never falls through to the generic
 * foot-strike handling (see the file header). Releases when the taker is
 * within THROW_TAKE_REACH of the spot and asks to play it, OR unconditionally
 * once the Ready window elapses (the bounded fallback — a throw-in can never
 * deadlock, per the authorization's timeout constraint).
 */
function runThrowInReady(state: WorldState, inputs: ReadonlyArray<InputCommand | null>): void {
  const m = state.match;
  gateInputs(state, inputs, true);

  const taker = m.restartTaker;
  const p = state.players;

  // Once armed, inputs cannot retrigger or accelerate the throw. The ball RIDES
  // the wind-up: it leaves the two-handed carry and arrives over the head across
  // the same THROW_RELEASE_CONTACT_S the authored clip spends raising the arms,
  // and detaches at the top. Parking it overhead for the whole countdown (what
  // this used to do) released the ball before the hands got there — invisible
  // head-on, blatant from the side, which is the angle the owner asks for.
  if (taker >= 0 && p.anim[taker] === AnimState.Throw && p.actionTimer[taker]! > 0) {
    const lift = clamp(1 - m.stageTimer / THROW_RELEASE_CONTACT_S, 0, 1);
    placeThrowInBall(state, taker, 0.5 + lift * 0.5);
    if (m.stageTimer <= 0) executeThrowIn(state, taker);
    return;
  }
  // THE PICKUP. Hands on the ball only once he is actually standing over it —
  // before that it stays pinned on the spot by placeBallForRestart, waiting for
  // him rather than flying up the touchline to meet him.
  const arrived = takerHasArrived(state, taker);
  const picked = throwInPickupFraction(state);
  if (arrived) placeThrowInBall(state, taker, picked * 0.5);

  let release = m.stageTimer <= 0;
  // He cannot throw a ball he has not finished picking up. The bounded fallback
  // above is unaffected (RESTART_READY_S 2.0 s ≫ THROW_PICKUP_S 0.45 s), so this
  // can only ever delay an EARLY take, never deadlock one.
  if (!release && arrived && picked >= 1 && taker >= 0) {
    const inp = inputs[taker];
    if (inp !== null && inp !== undefined && wantsToPlay(inp)) release = true;
  }
  if (!release) return;

  // Prefer the designated taker; if they no longer qualify (sent off mid-setup,
  // or STILL NOT AT THE SPOT when the Ready window lapsed), fall back to whoever
  // is nearest the spot now, so a valid squad always has someone to throw it in
  // and nobody throws a ball he is not holding.
  let thrower = taker;
  if (
    thrower < 0 || p.active[thrower] !== 1 || p.sentOff[thrower] === 1
    || p.team[thrower] !== m.restartTeam || !arrived
  ) {
    thrower = nearestActivePlayerOfTeam(state, m.restartTeam, m.restartX, m.restartZ);
  }
  if (thrower < 0) return; // no eligible player at all — hold, re-checked next tick

  armThrowInRelease(state, thrower);
}

// ---------------------------------------------------------------------------
// Staging orchestration — compute the marks, then walk everybody to them
// ---------------------------------------------------------------------------

/**
 * Rebuild every staged player's MARK for the current restart phase. Pure with
 * respect to WorldState — it writes only the module scratch buffers — so it is
 * safe to run both from runRestart (every Setup/Ready tick) and from
 * beginRestart (once, to measure how long the walk-in will take).
 *
 * MatchPhase.Kickoff is absent by design (hard reset — enforceKickoffFormation
 * still re-pins positions directly), and so is MatchPhase.DropBall, which has
 * never had a staged shape and does not gain one here.
 */
function computeStageTargets(state: WorldState): void {
  const m = state.match;
  clearStageTargets();
  if (m.phase === MatchPhase.Kickoff || m.phase === MatchPhase.Goal) {
    // Law 8's shape. `Goal` is here so the walk home happens DURING the
    // celebration dwell that already exists, rather than adding a new wait —
    // see stageKickoffDuringDwell.
    //
    // ⚠ `HalfTime` is deliberately NOT here. The ends swap
    // (`teamAAttacksPositiveX`) only when that dwell EXPIRES, so staging during
    // it would walk all 22 to the half they are about to leave and then walk
    // them back — strictly worse than the teleport. The half-time walk-in is
    // absorbed by sizeSetupWindowForWalkIn instead, which is affordable there
    // because a half-time interval is not the tight post-goal beat.
    stageKickoffShape(state);
  } else if (m.phase === MatchPhase.ThrowIn) {
    // Contextual choreography (playtest §8) first, THEN the Law 15 distance
    // clamp — the clamp is the final authority, so however the roles above
    // marked a defender, nobody is sent illegally close to the throw spot.
    enforceThrowInFormation(state);
    enforceThrowInDistance(state);
  } else if (m.phase === MatchPhase.Corner) {
    // Corner choreography enforces its own Law 17 distance internally (it
    // needs to run per-defender, after the marking roles are placed).
    enforceCornerFormation(state);
  } else if (m.phase === MatchPhase.FreeKick) {
    // Law 13: 9.15m clamp + a real wall when the kick threatens the goal.
    enforceFreeKickFormation(state);
  } else if (m.phase === MatchPhase.Penalty) {
    // Law 14: keeper on his line, box + arc cleared, taker on his mark.
    enforcePenaltyFormation(state);
  } else if (m.phase === MatchPhase.GoalKick) {
    // Law 16: opponents outside the penalty area until the ball is in play.
    enforceGoalKickFormation(state);
  }
}

/**
 * True when every CEREMONY-CRITICAL staged player is standing on his mark —
 * i.e. the set piece is SET and may legitimately be played.
 *
 * This is the contract sizeSetupWindowForWalkIn budgets for, expressed as a
 * predicate so it can be asserted directly (restart-walk-in.test.ts) rather
 * than inferred from "nobody moved this tick" — which would wrongly count the
 * rest-defence men still jogging home, who are deliberately not waited for.
 *
 * Recomputes the marks, which is idempotent within a tick (they are a stable
 * fixed point) and touches only module scratch, never WorldState.
 */
export function isRestartShapeSettled(state: WorldState): boolean {
  computeStageTargets(state);
  const worst = maxStageTravel(state);
  clearStageTargets();
  return worst <= RESTART_STAGE_SETTLED_EPSILON;
}

/**
 * Walk everyone toward their kick-off marks while the GOAL celebration dwells.
 *
 * Same mover, same gait ladder, same stage lock as every other walk-in — the
 * only difference is that this one runs in a phase that is not a restart, so it
 * has to be called explicitly rather than falling out of `runRestart`.
 *
 * The keeper is included and gets an honest clip through `gk-travel.ts` like
 * anywhere else. Nobody's inputs are live during a celebration (`gateInputs`
 * has already run), so this cannot fight a player for control.
 */
function stageKickoffDuringDwell(state: WorldState): void {
  computeStageTargets(state);
  applyStageTargets(state);
}

/**
 * Size this restart's SETUP window to the longest walk-in it actually needs.
 * Called once, from beginRestart, with the phase/spot/taker already set.
 *
 * Why it is computed once rather than polled: a per-tick "stay in Setup until
 * everyone has settled" gate is unbounded, and one player the AI keeps tugging
 * off his mark would hang the match forever — strictly worse than the teleport
 * it replaced. Measuring the real distances up front gives the same
 * "restart only when the shape is ready" guarantee with a countdown that is
 * bounded by construction: never shorter than the phase's base ceremony,
 * never longer than base + RESTART_STAGE_EXTRA_MAX_S.
 *
 * A nearby throw-in therefore still takes RESTART_SETUP_S exactly as before;
 * only a restart whose shape genuinely has to travel gets the extra seconds,
 * and the clock is stopped throughout either way (step.ts only advances
 * clockTicks while InPlay).
 */
function sizeSetupWindowForWalkIn(state: WorldState): void {
  const m = state.match;
  const base = setupDurationFor(m.phase);
  // Kick-off used to return here ("hard reset — nobody walks in"). It walks in
  // now, so it must be budgeted like any other restart or the Law-8 clamp at the
  // whistle becomes a teleport for whoever had not arrived. In practice the
  // extra is small and usually zero: the walk home already ran through the
  // GOAL_CELEBRATION_S / HALF_TIME_BREAK_S dwell (stageKickoffDuringDwell), so
  // by the time this is called most of the distance is behind them, and at
  // match start everyone spawns on their anchor with nothing to travel.
  computeStageTargets(state);
  // stageTravelTime, NOT distance/RESTART_STAGE_SPEED: the mover eases to a jog
  // and then a walk on the way in, so the run band alone under-counts the walk-in
  // and the ceremony would end with its last men still arriving.
  const travelS = stageTravelTime(maxStageTravel(state)) + RESTART_STAGE_SETTLE_PAD_S;
  clearStageTargets(); // don't leave a stale mark set behind for the next reader
  m.stageTimer = clamp(travelS, base, base + RESTART_STAGE_EXTRA_MAX_S);
}

// ---------------------------------------------------------------------------
// The state machine
// ---------------------------------------------------------------------------

/** Which team takes the kickoff for a given half. (§5.1) */
function kickoffTeamForHalf(half: number): number {
  return half === 1 ? 0 : 1;
}

function runRestart(
  state: WorldState,
  inputs: ReadonlyArray<InputCommand | null>,
  out: StepResult,
  humanMask: Uint8Array | null = null,
): void {
  const m = state.match;
  const p = state.players;

  // §Restart no-teleport: during an ACTIVE (in-play) restart — a throw-in, corner,
  // goal kick or free kick in visible play — the human-controlled player's
  // AUTHORITATIVE position must never be snapped by the staging below. They get a
  // target and move there manually; only the BOTS are placed into formation. A
  // KICK-OFF (match start / after a goal) is a hard reset and may still stage
  // everyone. We snapshot each human's kinematic state, let staging run (it snaps
  // everyone), then restore the humans — a single chokepoint, so no per-role
  // staging function needs to know about human control (which the sim otherwise
  // deliberately cannot tell from a bot, §10.1).
  const spareHumans = humanMask !== null && m.phase !== MatchPhase.Kickoff;
  if (spareHumans) {
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (humanMask![i] !== 1) continue;
      restartSaveX[i] = p.px[i]!; restartSaveZ[i] = p.pz[i]!; restartSaveY[i] = p.py[i]!;
      restartSaveVx[i] = p.vx[i]!; restartSaveVy[i] = p.vy[i]!; restartSaveVz[i] = p.vz[i]!;
    }
  }

  // Phase-specific staging, every Setup/Ready tick until Live: extends the
  // input-gating freeze below to POSITION and to the ball itself, so neither
  // AI nor a human can pre-empt the restart by standing where Law 8/15 forbid
  // it or by nudging a "dead" ball (playtest DEFECT #8; see the file header).
  if (m.phase === MatchPhase.Kickoff) {
    // Hard reset: kick-off is the one restart that still re-pins positions
    // instantly (see the walk-in section at the top of the file).
    enforceKickoffFormation(state);
    placeBallForRestart(state, m.restartX, m.restartZ);
  } else if (
    m.phase === MatchPhase.ThrowIn ||
    m.phase === MatchPhase.Corner ||
    m.phase === MatchPhase.FreeKick ||
    m.phase === MatchPhase.Penalty ||
    m.phase === MatchPhase.GoalKick
  ) {
    // Recompute this tick's marks (they are a stable fixed point, so this is
    // the same shape as last tick unless the world changed under it), then
    // walk everybody one capped step toward theirs. MatchPhase.DropBall is
    // deliberately absent from both lists — it has never been staged, and it
    // does not get a per-tick ball re-pin either.
    computeStageTargets(state);
    applyStageTargets(state);
    placeBallForRestart(state, m.restartX, m.restartZ);
  }

  // Restore the humans: bots stay staged; the human is exactly where they were,
  // free to walk to their target (the client shows the waypoint).
  if (spareHumans) {
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (humanMask![i] !== 1) continue;
      p.px[i] = restartSaveX[i]!; p.pz[i] = restartSaveZ[i]!; p.py[i] = restartSaveY[i]!;
      p.vx[i] = restartSaveVx[i]!; p.vy[i] = restartSaveVy[i]!; p.vz[i] = restartSaveVz[i]!;
      // …and hand him his locomotion back. applyStageTargets locked every player
      // it moved; a human was never really moved (this loop just undid it), so
      // leaving him locked would freeze the one player who is supposed to be
      // walking to his mark under his own control.
      clearStageLock(i);
    }
  }

  if (m.restartStage === RestartStage.Setup) {
    // Positioning window: nobody may play the ball yet — `takerMayPlay` is
    // false, so even the designated taker's Pass/Shoot bits are stripped. THIS
    // is what "the ball is in game only after the referee's whistle" already
    // means mechanically; the event below is the announcement of it.
    gateInputs(state, inputs, false);
    if (m.stageTimer <= 0) {
      m.restartStage = RestartStage.Ready;
      m.stageTimer = RESTART_READY_S;
      // ── THE REFEREE'S WHISTLE (Law 5; §5.1, §5.6) ─────────────────────────
      //
      // Owner report, verbatim: *"the ball is in game only after refree whisely
      // for start off , throwin , foul , corner / players has to wait for the
      // whistle to start the game"*.
      //
      // Setup→Ready IS that moment, and it always was: Setup gates the take
      // verbs off EVERYONE (above), Ready is the first stage in which the taker
      // may play the ball, and the flip happens after this function has already
      // returned — so the earliest possible strike is the tick AFTER this event.
      // What was missing was any way for a client to KNOW: `StepResult.events`
      // carried GOAL/FOUL/HALF_TIME/FULL_TIME and nothing for a restart being
      // signalled, so the client's audio layer could only infer a whistle from a
      // Kickoff→InPlay phase edge — which fires when the ball is actually
      // struck, one Ready window late, and never fires at all for a throw-in, a
      // corner, a goal kick, a free kick or a penalty.
      //
      // Raised here rather than at Ready→Live because the referee whistles
      // BEFORE the kick, not on it. `phase` carries which restart is being
      // signalled so the client can pick a pattern (a long blast to start a
      // half, a short one for a free kick); `player` is the taker, for a HUD cue.
      out.events.push({
        type: 'WHISTLE',
        tick: state.tick,
        team: m.restartTeam,
        player: m.restartTaker,
        phase: m.phase,
        x: m.restartX,
        z: m.restartZ,
      });
    }
    return;
  }

  if (m.restartStage === RestartStage.Ready) {
    // Throw-ins never fall through to the generic foot-strike path below —
    // see runThrowInReady and the file header for why (playtest DEFECT #7).
    if (m.phase === MatchPhase.ThrowIn) {
      runThrowInReady(state, inputs);
      return;
    }

    // The taker may play it; if they do (or the ready window lapses) we go live.
    const taker = m.restartTaker;
    let taken = false;
    if (taker >= 0) {
      const inp = inputs[taker];
      if (inp !== null && inp !== undefined && wantsToPlay(inp)) taken = true;
    }
    // Keep the taker's verbs, strip everyone else's, in every ready tick.
    gateInputs(state, inputs, true);
    if (taken || m.stageTimer <= 0) {
      // Remember which restart this is BEFORE the phase flips: the first kick
      // resolveKicks strikes in InPlay is the restart taken, and its offside
      // snapshot must be tagged with this phase for the Law-11 exemption (§5.4).
      // Consumed by that strike (ball.ts clears it back to InPlay).
      m.restartKickPhase = m.phase;
      m.phase = MatchPhase.InPlay;
      m.restartStage = RestartStage.Live;
      m.stageTimer = 0;
      m.possessionTeam = m.restartTeam;
    }
  }
}

/**
 * Advance the match FSM one tick. Runs before possession, control and ball
 * integration (step.ts stage 2). See the file header for the contract.
 */
export function stepMatchStateMachine(
  state: WorldState,
  inputs: ReadonlyArray<InputCommand | null>,
  out: StepResult,
  humanMask: Uint8Array | null = null,
): void {
  const m = state.match;

  // Rebuild the restart stage lock for THIS world and THIS tick, before anything
  // can set or read it. Unconditional on purpose: the buffer is module scope and
  // shared by every WorldState in the process, so a world that is not staging
  // anybody must still clear it, or a predicted world's corner could lock a
  // different world's players. See rules/stage-lock.ts.
  beginStageLockTick(state);

  switch (m.phase) {
    case MatchPhase.Warmup:
      // Kick the match off. The kickoff's own setup phase is the warmup.
      beginKickoff(state, kickoffTeamForHalf(m.half));
      return;

    case MatchPhase.HalfTime:
      if (m.stageTimer <= 0) {
        m.half = 2;
        m.clockTicks = 0;
        // First-half stoppage must not leak into the second half's length —
        // addedTimeTicks only ever accumulates (addStoppage), so without this
        // reset H2 runs long by the entire H1 added time.
        m.addedTimeTicks = 0;
        // Teams change ends at half time (§5.1).
        state.teamAAttacksPositiveX = !state.teamAAttacksPositiveX;
        beginKickoff(state, kickoffTeamForHalf(2));
      }
      return;

    case MatchPhase.Goal:
      // Celebration dwell. The ball is dead and stays in the net; nobody may
      // play it. When the timer runs out, stage the kickoff for the side that
      // conceded (beginGoalCelebration parked them in restartTeam).
      gateInputs(state, inputs, false);
      // ── THE WALK HOME HAPPENS HERE, INSIDE TIME THE GAME WAS ALREADY SPENDING ─
      //
      // This is what makes removing the kick-off teleport cost NOTHING. The
      // measured jump was up to 40.24 m, which is 7.3 s of walk-in at
      // RESTART_STAGE_SPEED — long enough that waiting for it in the kick-off
      // Setup window would have re-added most of the dead time an earlier
      // session cut (16.1 s → 7.3 s), and the owner has separately reported the
      // game feels slow.
      //
      // But GOAL_CELEBRATION_S is 5 s the match already stands still for, and
      // players jogging back to their halves while the scorer celebrates is
      // exactly what real football looks like. So the walk runs through the
      // celebration and is almost entirely finished before the kick-off window
      // even opens.
      //
      // Humans are NOT spared here, deliberately: a kick-off is the one restart
      // that stages everyone (runRestart's `spareHumans` excludes it), and the
      // human walking back with everyone else is the same thing his team-mates
      // are doing.
      //
      // ⚠ ORDER IS LOAD-BEARING — the hand-off tick must walk ONCE, not twice.
      // `beginKickoff` stages the shape itself (so no illegal frame is ever
      // visible), so calling the dwell walk first and then handing off ran
      // `applyStageTargets` TWICE in the same tick. MEASURED by the probe: a
      // single 0.1819 m step, exactly 2 x RESTART_STAGE_SPEED x dt, on a player
      // whose velocity correctly read 5.500 — a double-move, which is the same
      // class of defect as the teleport this whole change removes.
      if (m.stageTimer <= 0) {
        beginKickoff(state, m.restartTeam);
        return;
      }
      stageKickoffDuringDwell(state);
      return;

    case MatchPhase.FullTime:
      return; // match over — no further transitions

    case MatchPhase.PenaltyShootout:
      // The wrapper phase: between kicks. Its own module counts the pause down
      // and stages the next penalty, or ends the match when it is decided.
      stepShootout(state, out);
      return;

    case MatchPhase.InPlay: {
      // A LIVE SHOOTOUT KICK IS ALSO `InPlay` — runRestart flips the phase on
      // the strike, so without this the clock would keep ticking past full
      // time and re-award FullTime every tick, killing the shootout on the
      // frame it started. The attempt's own end conditions run instead.
      if (m.shootoutActive === 1) {
        stepShootoutAttempt(state, out);
        return;
      }
      const halfTicks =
        Math.round(state.config.halfLengthS * SIMULATION_TICK_RATE) + m.addedTimeTicks;
      if (m.clockTicks >= halfTicks) {
        if (m.half === 1) {
          m.phase = MatchPhase.HalfTime;
          m.restartStage = RestartStage.Setup;
          m.stageTimer = HALF_TIME_BREAK_S;
          out.events.push({ type: 'HALF_TIME', tick: state.tick, team: -1, player: -1 });
        } else if (m.scoreA === m.scoreB && state.config.shootoutOnDraw) {
          // "penality if draw" — a level match goes to penalties instead of
          // simply stopping. The score stays level in the record; the shootout
          // keeps its own tally and decides the winner.
          beginShootout(state, out);
        } else {
          m.phase = MatchPhase.FullTime;
          out.events.push({ type: 'FULL_TIME', tick: state.tick, team: -1, player: -1 });
        }
      }
      return;
    }

    default:
      // A restart phase set by this file or by a rule award. The stage timer is
      // counted down in seconds by step.ts stage 1; we only read it here.
      if (isRestartPhase(m.phase)) runRestart(state, inputs, out, humanMask);
      return;
  }
}
